import React, { useState, useEffect, useRef } from 'react';
// import { WaveDivider } from './components/WaveDivider';
import { Header } from './Header';
import { Footer } from './Footer';
import { FunctionPrismVisualization } from './components/FunctionPrismVisualization';

/**
 * BrushstrokeDivider - Watercolor brushstroke section divider
 */
const BrushstrokeDivider = ({ flip = false, bgColor = '#fff' }) => (
  <div style={{
    width: '100%',
    overflow: 'hidden',
    lineHeight: 0,
    backgroundColor: bgColor,
    transform: flip ? 'rotate(180deg)' : 'none'
  }}>
    <svg
      viewBox="0 0 1200 120"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: '60px',
        display: 'block'
      }}
    >
      <defs>
        <linearGradient id={`brushGradient${flip ? 'Flip' : ''}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(250, 190, 192, 0.6)" />
          <stop offset="35%" stopColor="rgba(247, 192, 138, 0.5)" />
          <stop offset="70%" stopColor="rgba(251, 244, 188, 0.4)" />
          <stop offset="100%" stopColor="rgba(247, 192, 138, 0.3)" />
        </linearGradient>
      </defs>
      <path
        d="M0,60 Q150,20 300,45 T600,30 T900,50 T1200,35 L1200,120 L0,120 Z"
        fill={`url(#brushGradient${flip ? 'Flip' : ''})`}
      />
      <path
        d="M0,75 Q200,40 400,60 T800,45 T1200,55 L1200,120 L0,120 Z"
        fill="rgba(250, 190, 192, 0.25)"
      />
    </svg>
  </div>
);

/**
 * HighlighterBarDivider - Centered gradient highlighter bar
 */
const HighlighterBarDivider = ({ bgColor = '#fff', width = '180px' }) => (
  <div style={{ backgroundColor: bgColor, padding: '40px 0' }}>
    <div
      style={{
        height: '8px',
        background: 'linear-gradient(90deg, #FABEC0 0%, #F7C08A 50%, #FBF4BC 100%)',
        margin: '0 auto',
        maxWidth: width,
        borderRadius: '4px',
        opacity: 0.6
      }}
    />
  </div>
);

/**
 * PrismScatterDivider - Scattered prism shapes divider
 */
const PrismScatterDivider = ({ bgColor = '#fff' }) => (
  <div style={{
    backgroundColor: bgColor,
    padding: '30px 0',
    position: 'relative',
    overflow: 'hidden',
    height: '60px'
  }}>
    <svg
      viewBox="0 0 1200 60"
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      {/* Scattered prism shapes */}
      <polygon points="100,45 130,25 160,45" fill="rgba(250, 190, 192, 0.5)" />
      <polygon points="280,35 305,20 330,40 310,50" fill="rgba(247, 192, 138, 0.4)" />
      <polygon points="450,40 480,15 510,40" fill="rgba(251, 244, 188, 0.5)" />
      <polygon points="620,30 650,45 680,30 650,15" fill="rgba(127, 175, 178, 0.4)" />
      <polygon points="800,42 825,22 850,42" fill="rgba(250, 190, 192, 0.45)" />
      <polygon points="950,35 980,20 1010,35 980,50" fill="rgba(247, 192, 138, 0.5)" />
      <polygon points="1100,40 1130,25 1160,45" fill="rgba(251, 244, 188, 0.45)" />
    </svg>
  </div>
);

/**
 * SoftWaveDivider - Gentle wave curve divider
 */
const SoftWaveDivider = ({ flip = false, bgColor = '#fff', waveColor = 'rgba(250, 190, 192, 0.3)' }) => (
  <div style={{
    width: '100%',
    overflow: 'hidden',
    lineHeight: 0,
    backgroundColor: bgColor,
    transform: flip ? 'rotate(180deg)' : 'none'
  }}>
    <svg
      viewBox="0 0 1200 80"
      preserveAspectRatio="none"
      style={{ width: '100%', height: '40px', display: 'block' }}
    >
      <path
        d="M0,40 Q300,80 600,40 T1200,40 L1200,80 L0,80 Z"
        fill={waveColor}
      />
    </svg>
  </div>
);

/**
 * GradientFadeDivider - Subtle gradient fade transition
 */
const GradientFadeDivider = ({ fromColor = '#fff', toColor = '#faf8f6' }) => (
  <div style={{
    height: '60px',
    background: `linear-gradient(180deg, ${fromColor} 0%, ${toColor} 100%)`
  }} />
);

/**
 * EvidenceBar - Full-width stats bar with warm background, colored numbers,
 * watercolor highlighters, and scroll-triggered counting animation
 */
const EVIDENCE_STATS = [
  {
    target: 60000,
    display: '60K',
    suffix: '+',
    label: 'verified patients ready to share their authentic healthcare experiences.',
    color: '#D4796A',
    highlightColor: 'rgba(212, 121, 106, 0.35)'
  },
  {
    target: 2500,
    display: '2,500',
    suffix: '+',
    label: 'health conditions represented across our diverse community.',
    color: '#C9A227',
    highlightColor: 'rgba(201, 162, 39, 0.3)'
  },
  {
    target: 98,
    display: '98',
    suffix: '%',
    label: 'of partners say patient insights improved their decisions.',
    color: '#7FAFB2',
    highlightColor: 'rgba(127, 175, 178, 0.35)'
  }
];

// eslint-disable-next-line no-unused-vars
const EvidenceBar = () => {
  const barRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState([0, 0, 0]);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;

          // Animate each number
          EVIDENCE_STATS.forEach((stat, index) => {
            const duration = 3000;
            const steps = 60;
            const increment = stat.target / steps;
            let current = 0;

            const timer = setInterval(() => {
              current += increment;
              if (current >= stat.target) {
                current = stat.target;
                clearInterval(timer);
              }
              setCounts(prev => {
                const newCounts = [...prev];
                newCounts[index] = Math.floor(current);
                return newCounts;
              });
            }, duration / steps);
          });
        }
      },
      {
        threshold: 0.6,
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    if (barRef.current) observer.observe(barRef.current);
    return () => observer.disconnect();
  }, []);

  // Format number with K for thousands
  const formatNumber = (num, index) => {
    if (index === 0) {
      // First stat: show as "60K" format
      if (num >= 1000) {
        return Math.floor(num / 1000) + 'K';
      }
      return num.toString();
    }
    // Other stats: show with commas
    return num.toLocaleString();
  };

  return (
    <div
      ref={barRef}
      style={{
        background: 'linear-gradient(180deg, #FAF8F6 0%, #fff 100%)',
        padding: '60px 40px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Subtle decorative prism shapes */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        left: '5%',
        width: '120px',
        height: '80px',
        background: 'linear-gradient(135deg, rgba(247, 192, 138, 0.15) 0%, rgba(247, 192, 138, 0.1) 100%)',
        clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)',
        transform: 'rotate(-8deg)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-15px',
        right: '8%',
        width: '100px',
        height: '60px',
        background: 'linear-gradient(135deg, rgba(127, 175, 178, 0.2) 0%, rgba(127, 175, 178, 0.1) 100%)',
        clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)',
        transform: 'rotate(12deg)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '25%',
        width: '80px',
        height: '50px',
        background: 'linear-gradient(135deg, rgba(251, 244, 188, 0.2) 0%, rgba(251, 244, 188, 0.1) 100%)',
        clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)',
        transform: 'rotate(-5deg)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '48px',
        position: 'relative',
        zIndex: 1,
        justifyItems: 'center'
      }}>
        {EVIDENCE_STATS.map((stat, index) => (
          <div
            key={index}
            style={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: `all 0.6s ease ${index * 0.15}s`
            }}
          >
            {/* Number with watercolor highlighter */}
            <div style={{
              position: 'relative',
              display: 'inline-block',
              marginBottom: '16px'
            }}>
              {/* Watercolor highlighter accent */}
              <span
                style={{
                  position: 'absolute',
                  left: '-6%',
                  right: '-6%',
                  bottom: '10%',
                  height: '38%',
                  background: stat.highlightColor,
                  transform: `skewX(-2deg) rotate(-1deg) ${isVisible ? 'scaleX(1)' : 'scaleX(0)'}`,
                  transformOrigin: 'left',
                  borderRadius: '3px 8px 6px 4px',
                  zIndex: 0,
                  transition: `transform 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${0.4 + index * 0.15}s`
                }}
              />
              {/* Main number */}
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(96px, 14vw, 150px)',
                  fontWeight: '700',
                  color: stat.color,
                  lineHeight: 1
                }}
              >
                {formatNumber(counts[index], index)}
              </span>
              {/* Suffix */}
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(48px, 7vw, 75px)',
                  fontWeight: '700',
                  color: stat.color,
                  verticalAlign: 'top',
                  marginLeft: '4px'
                }}
              >
                {stat.suffix}
              </span>
            </div>
            {/* Description */}
            <p style={{
              fontSize: '15px',
              lineHeight: '1.6',
              color: '#4A4A4A',
              margin: '0 auto',
              maxWidth: '260px'
            }}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Shape definitions for prism fragments - trapezoids, parallelograms, etc.
const prismShapes = [
  // Rectangles (classic)
  'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
  // Parallelograms (leaning right)
  'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)',
  'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)',
  // Parallelograms (leaning left)
  'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)',
  'polygon(0% 0%, 80% 0%, 100% 100%, 20% 100%)',
  // Trapezoids (wider at bottom)
  'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)',
  'polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%)',
  // Trapezoids (wider at top)
  'polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)',
  'polygon(0% 0%, 100% 0%, 85% 100%, 15% 100%)',
  // Rhomboids
  'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)',
  'polygon(0% 0%, 80% 0%, 100% 100%, 20% 100%)',
  // Chevrons / arrows
  'polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%)',
  // Pentagons
  'polygon(50% 0%, 100% 35%, 80% 100%, 20% 100%, 0% 35%)',
  // Asymmetric quads
  'polygon(5% 0%, 95% 5%, 90% 100%, 0% 90%)',
  'polygon(10% 5%, 100% 0%, 95% 95%, 5% 100%)',
];

// Gradient directions for inner light effect
const gradientDirections = [
  '135deg', '45deg', '180deg', '90deg', '225deg', '315deg'
];

/**
 * PrismConvergence - Scroll-triggered animation where geometric fragments converge
 * Visual metaphor: Individual patient voices coming together to form complete insights
 */
const PrismConvergence = ({ trustSectionPassed = false }) => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Color palette with brand color pairs for gradients
  const colorPairs = [
    { from: '#FABEC0', to: '#F7C08A' },  // Blush to apricot
    { from: '#F7C08A', to: '#FBF4BC' },  // Apricot to yellow
    { from: '#FBF4BC', to: '#FABEC0' },  // Yellow to blush
    { from: '#FABEC0', to: '#FBF4BC' },  // Blush to yellow
    { from: '#F7C08A', to: '#FABEC0' },  // Apricot to blush
    { from: '#FABEC0', to: '#FABEC0' },  // Blush solid
    { from: '#FBF4BC', to: '#F7C08A' },  // Yellow to apricot
    { from: '#F7C08A', to: '#F7C08A' },  // Apricot solid
  ];

  // Generate fragments scattered across the visible landing page (reduced density)
  const generateFragments = () => {
    const fragments = [];
    const count = 40; // Reduced from 60 for cleaner hero appearance

    for (let i = 0; i < count; i++) {
      // Scatter fragments randomly across the entire viewport
      // Horizontal: full width and beyond
      let startX = (Math.random() - 0.5) * 180; // -90vw to +90vw

      // Vertical: scattered across entire visible screen height
      let startY = (Math.random() - 0.5) * 100; // -50vh to +50vh from center

      // Push fragments away from center zone where hero text appears
      // If fragment is in the center zone, push it outward
      const inCenterX = Math.abs(startX) < 35;
      const inCenterY = Math.abs(startY) < 20;
      if (inCenterX && inCenterY) {
        // Push vertically away from center
        startY = startY >= 0 ? startY + 25 : startY - 25;
      }

      // End position (spread around the heading - top, bottom, sides)
      const endAngle = Math.random() * Math.PI * 2;
      const endDistance = 4 + Math.random() * 14; // Tighter cluster (4-18 range)
      const endX = Math.cos(endAngle) * endDistance; // Horizontal spread
      // Vertical spread - centered around heading
      const endY = Math.sin(endAngle) * endDistance * 0.5;

      // Size variation - mix of sizes
      const sizeCategory = Math.random();
      let width, height;
      if (sizeCategory < 0.25) {
        // Large
        width = 110 + Math.random() * 90;
        height = 70 + Math.random() * 80;
      } else if (sizeCategory < 0.55) {
        // Medium
        width = 65 + Math.random() * 55;
        height = 45 + Math.random() * 55;
      } else {
        // Small
        width = 35 + Math.random() * 40;
        height = 25 + Math.random() * 35;
      }

      const colorPair = colorPairs[Math.floor(Math.random() * colorPairs.length)];
      const gradientDir = gradientDirections[Math.floor(Math.random() * gradientDirections.length)];

      fragments.push({
        id: i,
        width,
        height,
        colorFrom: colorPair.from,
        colorTo: colorPair.to,
        gradientDir,
        shape: prismShapes[Math.floor(Math.random() * prismShapes.length)],
        startX,
        startY,
        endX,
        endY,
        startRotate: (Math.random() - 0.5) * 100, // -50 to +50
        endRotate: (Math.random() - 0.5) * 30, // -15 to +15 when converged
        delay: Math.random() * 0.45, // Staggered delays
        zIndex: Math.floor(Math.random() * 10)
      });
    }

    return fragments;
  };

  // Memoize fragments so they don't regenerate on every render
  const [fragments] = useState(generateFragments);

  // Track if fragments have locked in place (once converged, they stay)
  const [isLocked, setIsLocked] = useState(false);

  // Delay subtext appearance until reader has moment with headline
  const [showSubtext, setShowSubtext] = useState(false);

  useEffect(() => {
    if (isLocked && !showSubtext) {
      // Give reader 800ms to absorb the headline after convergence
      const timer = setTimeout(() => setShowSubtext(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isLocked, showSubtext]);

  useEffect(() => {
    // Once locked, don't listen to scroll anymore
    if (isLocked) return;

    const handleScroll = () => {
      if (!sectionRef.current) return;

      // Don't start moving fragments until Trust section has passed
      if (!trustSectionPassed) {
        setScrollProgress(0);
        return;
      }

      const windowHeight = window.innerHeight;
      const sectionTop = sectionRef.current.offsetTop;
      const scrolled = window.scrollY;

      // Calculate how far we've scrolled into this section
      const sectionProgress = scrolled - (sectionTop - windowHeight);

      // Fragments should fully converge by the time we've scrolled 1 viewport height into the section
      // This gives the remaining scroll distance for viewing the converged state
      const convergenceTarget = windowHeight * 0.8;

      // Linear progress from 0 to 1
      const linearProgress = Math.max(0, sectionProgress / convergenceTarget);

      // Apply ease-in curve so fragments stay scattered longer
      // and only really come together in the final stretch
      const progress = Math.pow(linearProgress, 2);

      const newProgress = Math.min(1, Math.max(0, progress));
      setScrollProgress(newProgress);

      // Lock fragments as soon as they go behind the text (at 0.95)
      // This syncs with the zIndex change so they lock immediately when behind
      if (newProgress >= 0.95) {
        setScrollProgress(1); // Snap to fully converged
        setIsLocked(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLocked, trustSectionPassed]);

  // Easing function (ease-out-quart for smooth deceleration)
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  // Interpolate between start and end values
  const lerp = (start, end, progress) => start + (end - start) * progress;

  // Calculate individual fragment progress with delay
  const getFragmentProgress = (delay) => {
    const adjustedProgress = Math.max(0, (scrollProgress - delay) / (1 - delay));
    return easeOutQuart(Math.min(1, adjustedProgress));
  };

  // Fragments stay visible after converging (no fade out)

  return (
    <section
      ref={sectionRef}
      style={{
        height: '200vh', // Extra height to ensure animation completes with time to view
        position: 'relative',
        backgroundColor: '#F9F7F4'
      }}
    >
      {/* Scroll snap target - creates a stopping point when fragments converge */}
      <div
        style={{
          position: 'absolute',
          top: '100vh',
          height: '1px',
          width: '100%',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always'
        }}
      />
      {/* Fragment container - fixed during convergence, hidden after section passes */}
      {!isLocked && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '100%',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 15
          }}
        >
          {/* Fragments - positioned relative to viewport center */}
          {fragments.map((fragment) => {
            const progress = getFragmentProgress(fragment.delay);
            const x = lerp(fragment.startX, fragment.endX, progress);
            const y = lerp(fragment.startY, fragment.endY, progress);
            const rotate = lerp(fragment.startRotate, fragment.endRotate, progress);
            // Start subtle (0.2), become more visible as they converge (0.7)
            const opacity = 0.2 + progress * 0.5;
            const scale = 0.7 + progress * 0.3;

            return (
              <div
                key={fragment.id}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${fragment.width}px`,
                  height: `${fragment.height}px`,
                  background: `linear-gradient(${fragment.gradientDir}, ${fragment.colorFrom} 0%, ${fragment.colorTo} 100%)`,
                  opacity: opacity,
                  transform: `translate(calc(-50% + ${x}vw), calc(-50% + ${y}vh)) rotate(${rotate}deg) scale(${scale})`,
                  willChange: 'transform, opacity',
                  zIndex: fragment.zIndex,
                  clipPath: fragment.shape,
                  boxShadow: `0 4px 20px rgba(0, 0, 0, 0.08)`,
                  backgroundImage: `linear-gradient(${fragment.gradientDir}, ${fragment.colorFrom} 0%, ${fragment.colorTo} 100%), linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 50%)`
                }}
              />
            );
          })}
        </div>
      )}

      {/* Sticky content container - stays in viewport while scrolling through section */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          zIndex: 10
        }}
      >
        {/* Locked fragments - rendered inside sticky container so they scroll with text */}
        {isLocked && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            {fragments.map((fragment) => {
              // When locked, all fragments are at their final position
              const x = fragment.endX;
              const y = fragment.endY;
              const rotate = fragment.endRotate;

              return (
                <div
                  key={fragment.id}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: `${fragment.width}px`,
                    height: `${fragment.height}px`,
                    background: `linear-gradient(${fragment.gradientDir}, ${fragment.colorFrom} 0%, ${fragment.colorTo} 100%)`,
                    opacity: 0.7,
                    transform: `translate(calc(-50% + ${x}vw), calc(-50% + ${y}vh)) rotate(${rotate}deg) scale(1)`,
                    zIndex: fragment.zIndex,
                    clipPath: fragment.shape,
                    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.08)`,
                    backgroundImage: `linear-gradient(${fragment.gradientDir}, ${fragment.colorFrom} 0%, ${fragment.colorTo} 100%), linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 50%)`
                  }}
                />
              );
            })}
          </div>
        )}
        {/* Center content - fades in as fragments converge */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            opacity: scrollProgress > 0.75 ? (scrollProgress - 0.75) / 0.25 : 0,
            transition: 'opacity 0.3s ease',
            zIndex: 100,
            padding: '40px'
          }}
        >
          <h3
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(36px, 5vw, 56px)',
              fontWeight: '600',
              color: '#111111',
              margin: 0,
              letterSpacing: '-0.5px'
            }}
          >
            Real patients are multidimensional.
          </h3>
        </div>

        {/* Subtext - appears BELOW the prism after pieces converge + delay */}
        <p
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, 240px)',
            textAlign: 'center',
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(18px, 2.5vw, 24px)',
            color: '#4A4A4A',
            fontStyle: 'italic',
            margin: 0,
            padding: '0 20px',
            maxWidth: '600px',
            opacity: showSubtext ? 1 : 0,
            transition: 'opacity 0.6s ease',
            zIndex: 100
          }}
        >
          Their experience deserves a <span style={{
            background: 'linear-gradient(180deg, transparent 50%, rgba(127, 175, 178, 0.4) 50%)',
            padding: '0 4px'
          }}>more complete view.</span>
        </p>
      </div>

    </section>
  );
};

/**
 * StorySlider - Premium horizontal carousel with sharp, cohesive design
 * Narrative flow: Approach → Application → Snapshot → Proof → Framework
 */
// eslint-disable-next-line no-unused-vars
const StorySlider = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Slides alternate between content and quotes
  const slides = [
    { id: 'approach', label: 'The Shift', number: '01', color: '#FABEC0', darkColor: '#F7C08A', type: 'content' },
    { id: 'quote1', label: '', number: '', color: '#FABEC0', darkColor: '#F7C08A', type: 'quote' },
    { id: 'application', label: 'The Application', number: '02', color: '#FBF4BC', darkColor: '#FBF4BC', type: 'content' },
    { id: 'quote2', label: '', number: '', color: '#FBF4BC', darkColor: '#FBF4BC', type: 'quote' },
    { id: 'snapshot', label: 'The Snapshot', number: '03', color: '#7FAFB2', darkColor: '#7FAFB2', type: 'content' },
    { id: 'quote3', label: '', number: '', color: '#7FAFB2', darkColor: '#7FAFB2', type: 'quote' },
    { id: 'proof', label: 'The Proof', number: '04', color: '#F7C08A', darkColor: '#F7C08A', type: 'content' },
    { id: 'quote4', label: '', number: '', color: '#F7C08A', darkColor: '#F7C08A', type: 'quote' },
    { id: 'framework', label: 'The Framework', number: '05', color: '#FABEC0', darkColor: '#F7C08A', type: 'content' }
  ];

  // Quote data for interstitial slides
  const quotes = {
    quote1: {
      text: "We used to guess what patients wanted. Now we",
      highlight: "actually listen to them",
      textAfter: "—and our protocols reflect that.",
      author: "Dr. Michael Torres",
      role: "Chief Medical Officer",
      company: "Rare Disease Biotech"
    },
    quote2: {
      text: "Every department was solving different problems. Real Patients helped us see",
      highlight: "one unified patient journey",
      textAfter: "across all our work.",
      author: "Jennifer Walsh",
      role: "VP Patient Strategy",
      company: "Global Pharma"
    },
    quote3: {
      text: "The depth of insight was remarkable. We discovered",
      highlight: "patterns we never knew existed",
      textAfter: "in our patient population.",
      author: "Dr. Anika Patel",
      role: "Head of Patient Insights",
      company: "Specialty Therapeutics"
    },
    quote4: {
      text: "The results speak for themselves. Our last three launches",
      highlight: "exceeded enrollment targets by 40%",
      textAfter: "because we understood what patients actually needed.",
      author: "Robert Chen",
      role: "Director of Clinical Operations",
      company: "Mid-Size Biotech"
    }
  };

  // Helper to render a quote slide
  const renderQuoteSlide = (quoteId, accentColor) => {
    const q = quotes[quoteId];
    return (
      <div style={{
        padding: '60px 48px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        background: `linear-gradient(135deg, ${accentColor}08 0%, #fff 50%, ${accentColor}05 100%)`
      }}>
        <div style={{ position: 'relative', marginBottom: '40px', maxWidth: '700px' }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '180px',
            color: accentColor,
            opacity: 0.15,
            fontFamily: 'Georgia, serif',
            lineHeight: 1,
            userSelect: 'none'
          }}>"</div>
          <p style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            fontWeight: '400',
            color: '#111111',
            lineHeight: '1.6',
            position: 'relative',
            zIndex: 1
          }}>
            {q.text}{' '}
            <span style={{
              position: 'relative',
              display: 'inline',
              whiteSpace: 'nowrap'
            }}>
              <span style={{
                position: 'absolute',
                left: '-3%',
                right: '-3%',
                bottom: '5%',
                height: '35%',
                background: `linear-gradient(90deg, ${accentColor} 0%, ${accentColor}90 100%)`,
                opacity: 0.5,
                transform: 'skewX(-2deg)',
                borderRadius: '3px',
                zIndex: 0
              }} />
              <span style={{ position: 'relative', zIndex: 1, fontWeight: '500' }}>{q.highlight}</span>
            </span>
            {q.textAfter}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: '#fff',
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontWeight: '600',
            boxShadow: `0 6px 20px ${accentColor}40`
          }}>{q.author.charAt(0)}</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#111111' }}>{q.author}</div>
            <div style={{ fontSize: '13px', color: '#888' }}>{q.role} • {q.company}</div>
          </div>
        </div>
      </div>
    );
  };

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // Shared card style for elevated, premium feel - fixed height for consistency
  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '24px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    transition: 'all 0.3s ease',
    height: '520px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden'
  };

  return (
    <section style={{
      padding: '60px 0 60px',
      background: 'linear-gradient(180deg, #F9F7F4 0%, #fff 100%)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Subtle decorative elements */}
      <div style={{ position: 'absolute', top: '60px', left: '5%', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(250,190,192,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '100px', right: '8%', width: '250px', height: '250px', background: 'radial-gradient(circle, rgba(181,201,168,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

      {/* Section header - more refined */}
      <div style={{ textAlign: 'center', marginBottom: '40px', padding: '0 40px', position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#999', marginBottom: '16px', fontWeight: '500' }}>
          How It Works
        </div>
        <h2 style={{
          fontFamily: '"Source Serif Pro", Georgia, serif',
          fontSize: 'clamp(36px, 5vw, 56px)',
          fontWeight: '600',
          color: '#111111',
          marginBottom: '20px',
          letterSpacing: '-0.5px'
        }}>
          The <span style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{
              position: 'absolute',
              left: '-4%',
              right: '-4%',
              bottom: '8%',
              height: '32%',
              background: 'linear-gradient(90deg, #FABEC0 0%, #FBF4BC 50%, #7FAFB2 100%)',
              opacity: 0.45,
              transform: 'skewX(-2deg)',
              borderRadius: '4px',
              zIndex: 0
            }} />
            <span style={{ position: 'relative', zIndex: 1 }}>complete picture</span>
          </span>
        </h2>
        <p style={{ fontSize: '18px', color: '#4A4A4A', maxWidth: '550px', margin: '0 auto', lineHeight: '1.7' }}>
          From fragmented data to unified insights—see how we transform patient understanding
        </p>
      </div>

      {/* Premium tab navigation - only show content slides */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '6px',
        marginBottom: '32px',
        padding: '0 20px'
      }}>
        <div style={{
          display: 'inline-flex',
          gap: '4px',
          padding: '6px',
          backgroundColor: '#F9F7F4',
          borderRadius: '16px'
        }}>
          {slides.filter(s => s.type === 'content').map((slide) => {
            const slideIndex = slides.findIndex(s => s.id === slide.id);
            const isActive = activeSlide === slideIndex || activeSlide === slideIndex + 1;
            return (
              <button
                key={slide.id}
                onClick={() => setActiveSlide(slideIndex)}
                style={{
                  padding: '14px 28px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: isActive ? '#fff' : 'transparent',
                  boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                  color: isActive ? '#111111' : '#888',
                  fontWeight: isActive ? '600' : '500',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: isActive ? slide.darkColor : '#bbb',
                  transition: 'color 0.25s ease'
                }}>
                  {slide.number}
                </span>
                {slide.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main slide container */}
      <div
        style={{
          position: 'relative',
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '0 60px'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Premium arrow buttons */}
        <button
          onClick={prevSlide}
          style={{
            position: 'absolute',
            left: '0',
            top: '50%',
            transform: `translateY(-50%) scale(${isHovered ? 1 : 0.9})`,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '2px solid #e8e8e8',
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: '#111111',
            zIndex: 10,
            transition: 'all 0.3s ease',
            opacity: isHovered ? 1 : 0.7,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button
          onClick={nextSlide}
          style={{
            position: 'absolute',
            right: '0',
            top: '50%',
            transform: `translateY(-50%) scale(${isHovered ? 1 : 0.9})`,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '2px solid #e8e8e8',
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: '#111111',
            zIndex: 10,
            transition: 'all 0.3s ease',
            opacity: isHovered ? 1 : 0.7,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        {/* Slides container */}
        <div style={{ overflow: 'hidden', borderRadius: '24px', margin: '0 60px' }}>
          <div style={{
            display: 'flex',
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: `translateX(-${activeSlide * 100}%)`
          }}>

            {/* SLIDE 1: The Shift */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{
                ...cardStyle,
                padding: '40px 36px',
                background: 'linear-gradient(135deg, #fff 0%, #fefefe 100%)'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr', gap: '24px', alignItems: 'center' }}>
                  {/* Before */}
                  <div style={{
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e8e8e8',
                    backgroundColor: '#F9F7F4'
                  }}>
                    <div style={{
                      display: 'inline-block',
                      fontSize: '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                      color: '#999',
                      backgroundColor: '#fff',
                      padding: '5px 10px',
                      borderRadius: '16px',
                      marginBottom: '14px',
                      border: '1px solid #eee'
                    }}>Before</div>
                    <h3 style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: '20px', fontWeight: '600', color: '#888', marginBottom: '14px' }}>Fragmented Data</h3>
                    <div style={{ position: 'relative', height: '90px', marginBottom: '16px' }}>
                      {[
                        { top: '5%', left: '8%', w: 45, h: 30, rot: -12, op: 0.5 },
                        { top: '25%', left: '55%', w: 38, h: 26, rot: 18, op: 0.4 },
                        { top: '55%', left: '25%', w: 34, h: 38, rot: -6, op: 0.55 },
                        { top: '60%', left: '65%', w: 30, h: 34, rot: -22, op: 0.45 }
                      ].map((p, i) => (
                        <div key={i} style={{
                          position: 'absolute',
                          top: p.top,
                          left: p.left,
                          width: `${p.w}px`,
                          height: `${p.h}px`,
                          background: 'linear-gradient(135deg, #d8d8d8, #c0c0c0)',
                          transform: `rotate(${p.rot}deg)`,
                          clipPath: 'polygon(12% 0%, 100% 0%, 88% 100%, 0% 100%)',
                          opacity: p.op
                        }} />
                      ))}
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#999', fontSize: '12px' }}>
                      {['Disconnected surveys', 'Point-in-time snapshots', 'Siloed patient voices'].map((text, i) => (
                        <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#bbb' }}>✕</span>
                          {text}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Arrow */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FABEC0 0%, #FBF4BC 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 20px rgba(250,190,192,0.4)'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>
                  </div>

                  {/* After */}
                  <div style={{
                    padding: '24px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #FFF9F8 0%, #FFFBFA 100%)',
                    border: '2px solid #FABEC0',
                    boxShadow: '0 6px 24px rgba(250,190,192,0.15)'
                  }}>
                    <div style={{
                      display: 'inline-block',
                      fontSize: '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                      color: '#F7C08A',
                      backgroundColor: '#fff',
                      padding: '5px 10px',
                      borderRadius: '16px',
                      marginBottom: '14px',
                      border: '1px solid rgba(212,121,106,0.2)'
                    }}>After</div>
                    <h3 style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: '20px', fontWeight: '600', color: '#111111', marginBottom: '14px' }}>Unified Insights</h3>
                    <div style={{ position: 'relative', height: '90px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ position: 'relative', width: '90px', height: '90px' }}>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-4deg)', width: '70px', height: '50px', background: 'linear-gradient(135deg, #FABEC0, #F7C08A)', clipPath: 'polygon(12% 0%, 100% 0%, 88% 100%, 0% 100%)', boxShadow: '0 3px 12px rgba(250,190,192,0.3)' }} />
                        <div style={{ position: 'absolute', top: '48%', left: '54%', transform: 'translate(-50%, -50%) rotate(7deg)', width: '62px', height: '45px', background: 'linear-gradient(135deg, #FBF4BC, #F5C49A)', clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)' }} />
                        <div style={{ position: 'absolute', top: '54%', left: '47%', transform: 'translate(-50%, -50%) rotate(-10deg)', width: '58px', height: '42px', background: 'linear-gradient(135deg, #7FAFB2, #A8C4A0)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)' }} />
                      </div>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#111111', fontSize: '12px' }}>
                      {['13 integrated data domains', 'Continuous, living data', '60K+ connected voices'].map((text, i) => (
                        <li key={i} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: 'rgba(107,154,122,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#7FAFB2' }}>✓</span>
                          {text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* QUOTE 1: After The Shift */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ ...cardStyle }}>
                {renderQuoteSlide('quote1', '#FABEC0')}
              </div>
            </div>

            {/* SLIDE 2: The Application */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{
                ...cardStyle,
                padding: '40px 36px'
              }}>
                <p style={{ fontSize: '16px', color: '#4A4A4A', maxWidth: '600px', margin: '0 auto 28px', textAlign: 'center', lineHeight: '1.6' }}>
                  Transform patient insights into strategic advantage
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                  {[
                    { title: 'Clinical Trial Design', desc: 'Understand patient burden, preferences, and barriers before your protocol is finalized.', result: '40% fewer amendments', icon: '◈', color: '#D4796A', bg: 'rgba(212,121,106,0.08)' },
                    { title: 'Market Access', desc: 'Build value stories with real patient evidence that payers and HTA bodies trust.', result: '3 HTA wins', icon: '◉', color: '#C9A227', bg: 'rgba(201,162,39,0.08)' },
                    { title: 'Patient Journey', desc: 'See beyond clinical touchpoints to understand daily life, workarounds, and unmet needs.', result: '5 new interventions', icon: '◆', color: '#7FAFB2', bg: 'rgba(127,175,178,0.08)' }
                  ].map((item, i) => (
                    <div key={i} style={{
                      padding: '32px 28px',
                      borderRadius: '20px',
                      backgroundColor: '#F9F7F4',
                      border: '1px solid #f0f0f0',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${item.color}, ${item.color}88)`
                      }} />
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '14px',
                        backgroundColor: item.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                        fontSize: '20px',
                        color: item.color
                      }}>
                        {item.icon}
                      </div>
                      <h3 style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: '20px', fontWeight: '600', color: '#111111', marginBottom: '12px' }}>{item.title}</h3>
                      <p style={{ fontSize: '14px', color: '#4A4A4A', lineHeight: '1.6', marginBottom: '20px' }}>{item.desc}</p>
                      <div style={{
                        fontSize: '14px',
                        color: item.color,
                        fontWeight: '600',
                        padding: '10px 16px',
                        backgroundColor: item.bg,
                        borderRadius: '10px',
                        display: 'inline-block'
                      }}>
                        {item.result}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* QUOTE 2: After The Application */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ ...cardStyle }}>
                {renderQuoteSlide('quote2', '#FBF4BC')}
              </div>
            </div>

            {/* SLIDE 3: The Snapshot */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{
                ...cardStyle,
                padding: '32px 36px',
                background: 'linear-gradient(135deg, #F9F7F4 0%, #fff 100%)'
              }}>
                <p style={{ fontSize: '16px', color: '#4A4A4A', maxWidth: '550px', margin: '0 auto 20px', textAlign: 'center', lineHeight: '1.6' }}>
                  A glimpse of what comprehensive patient insights look like
                </p>
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.04)',
                  maxWidth: '100%',
                  margin: '0 auto',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}>
                  <div style={{ backgroundColor: '#F9F7F4', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #eee' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#FF5F57' }} />
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#FFBD2E' }} />
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#28CA41' }} />
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: '#999', fontWeight: '500' }}>insights.realpatients.com</div>
                  </div>
                  <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px' }}>
                    <div style={{ backgroundColor: '#FFF9F8', borderRadius: '12px', padding: '16px', border: '1px solid rgba(250,190,192,0.25)' }}>
                      <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#D4796A', marginBottom: '10px', fontWeight: '600' }}>Key Insight</div>
                      <h4 style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: '16px', fontWeight: '600', color: '#111111', marginBottom: '12px', lineHeight: '1.4' }}>
                        73% of patients report <span style={{ backgroundColor: 'rgba(250,190,192,0.35)', padding: '1px 4px', borderRadius: '3px' }}>treatment fatigue</span> as primary reason for non-adherence
                      </h4>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#888' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#D4796A' }} />4,231 patients</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#111111' }} />12 conditions</span>
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#F9F7F4', borderRadius: '12px', padding: '16px' }}>
                      <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '12px', fontWeight: '600' }}>Coverage</div>
                      {[{ name: 'Daily Life', pct: 92, color: '#D4796A' }, { name: 'Treatment', pct: 87, color: '#FABEC0' }, { name: 'Navigation', pct: 78, color: '#7FAFB2' }].map((d, i) => (
                        <div key={i} style={{ marginBottom: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}><span style={{ color: '#111111', fontWeight: '500' }}>{d.name}</span><span style={{ color: '#888' }}>{d.pct}%</span></div>
                          <div style={{ height: '6px', backgroundColor: '#e8e8e8', borderRadius: '3px', overflow: 'hidden' }}><div style={{ width: `${d.pct}%`, height: '100%', backgroundColor: d.color, borderRadius: '3px' }} /></div>
                        </div>
                      ))}
                    </div>
                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      {[{ quote: "The side effects weren't the hardest part—it was explaining them to my employer.", attr: "Autoimmune patient" }, { quote: "Managing symptoms became my second full-time job.", attr: "Rare disease patient" }].map((q, i) => (
                        <div key={i} style={{ backgroundColor: '#FFF9F8', borderRadius: '10px', padding: '14px', borderLeft: `3px solid ${i === 0 ? '#FABEC0' : '#FBF4BC'}` }}>
                          <p style={{ fontSize: '12px', fontStyle: 'italic', color: '#111111', margin: '0 0 8px 0', lineHeight: '1.45' }}>"{q.quote}"</p>
                          <div style={{ fontSize: '10px', color: '#999', fontWeight: '500' }}>— {q.attr}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* QUOTE 3: After The Snapshot */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ ...cardStyle }}>
                {renderQuoteSlide('quote3', '#7FAFB2')}
              </div>
            </div>

            {/* SLIDE 4: The Proof */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{
                ...cardStyle,
                padding: '40px 36px',
                background: 'linear-gradient(135deg, #FFF9F8 0%, #FFFBFA 100%)',
                textAlign: 'center'
              }}>
                <div style={{ position: 'relative', marginBottom: '36px' }}>
                  <div style={{ position: 'absolute', top: '-30px', left: '50%', transform: 'translateX(-50%)', fontSize: '140px', color: '#FABEC0', opacity: 0.2, fontFamily: 'Georgia, serif', lineHeight: 1, userSelect: 'none' }}>"</div>
                  <p style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: '400', color: '#111111', lineHeight: '1.55', position: 'relative', zIndex: 1, maxWidth: '750px', margin: '0 auto' }}>
                    We thought we understood our patients. Real Patients showed us the <span style={{ background: 'linear-gradient(90deg, rgba(250,190,192,0.4), rgba(245,215,170,0.4))', padding: '2px 8px', borderRadius: '6px' }}>80% of their journey</span> we were completely missing.
                  </p>
                </div>
                <div style={{ marginBottom: '40px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FABEC0 0%, #F7C08A 100%)',
                    margin: '0 auto 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#fff',
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontWeight: '600',
                    boxShadow: '0 8px 24px rgba(232,146,124,0.3)'
                  }}>S</div>
                  <div style={{ fontSize: '17px', fontWeight: '600', color: '#111111' }}>Sarah Chen</div>
                  <div style={{ fontSize: '14px', color: '#888', marginTop: '4px' }}>VP of Patient Insights • Top 10 Pharma</div>
                </div>
                <div style={{
                  display: 'inline-flex',
                  gap: '40px',
                  padding: '28px 48px',
                  backgroundColor: '#fff',
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  margin: '0 auto'
                }}>
                  {[{ num: '47%', label: 'Faster insights', color: '#D4796A' }, { num: '3×', label: 'Deeper understanding', color: '#C9A227' }, { num: '12', label: 'Successful launches', color: '#7FAFB2' }].map((s, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', fontWeight: '700', color: s.color, fontFamily: '"Source Serif Pro", Georgia, serif' }}>{s.num}</div>
                      <div style={{ fontSize: '13px', color: '#4A4A4A', marginTop: '4px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* QUOTE 4: After The Proof */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ ...cardStyle }}>
                {renderQuoteSlide('quote4', '#F7C08A')}
              </div>
            </div>

            {/* SLIDE 5: The Framework */}
            <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{
                ...cardStyle,
                padding: '40px 36px'
              }}>
                <p style={{ fontSize: '16px', color: '#4A4A4A', maxWidth: '550px', margin: '0 auto 32px', textAlign: 'center', lineHeight: '1.6' }}>
                  Every facet of patient experience, unified into actionable insights
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '40px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-end' }}>
                    {[{ name: 'Diagnosis Journey', color: '#F7C08A' }, { name: 'Treatment Experience', color: '#FABEC0' }, { name: 'Daily Life Impact', color: '#F7C08A' }].map((f, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px 24px',
                        backgroundColor: '#F9F7F4',
                        borderRadius: '14px',
                        borderRight: `5px solid ${f.color}`,
                        minWidth: '190px',
                        transition: 'all 0.2s ease'
                      }}>
                        <span style={{ fontSize: '14px', color: '#111111', fontWeight: '500' }}>{f.name}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ position: 'relative', width: '160px', height: '160px' }}>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '140px',
                      height: '140px',
                      background: 'linear-gradient(135deg, rgba(250,190,192,0.7) 0%, rgba(245,215,170,0.6) 50%, rgba(181,201,168,0.7) 100%)',
                      clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                      boxShadow: '0 12px 40px rgba(250,190,192,0.35)'
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '95px',
                      height: '95px',
                      background: 'linear-gradient(135deg, #fff 0%, #fefefe 100%)',
                      clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.04)'
                    }}>
                      <span style={{ fontFamily: '"Source Serif Pro", Georgia, serif', fontSize: '13px', fontWeight: '600', color: '#111111', textAlign: 'center', lineHeight: '1.3' }}>Complete<br/>Picture</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-start' }}>
                    {[{ name: 'Emotional Wellbeing', color: '#FBF4BC' }, { name: 'Care Navigation', color: '#7FAFB2' }, { name: 'Support Systems', color: '#7FAFB2' }].map((f, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px 24px',
                        backgroundColor: '#F9F7F4',
                        borderRadius: '14px',
                        borderLeft: `5px solid ${f.color}`,
                        minWidth: '190px',
                        transition: 'all 0.2s ease'
                      }}>
                        <span style={{ fontSize: '14px', color: '#111111', fontWeight: '500' }}>{f.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{ textAlign: 'center', marginTop: '48px', fontSize: '15px', color: '#888', maxWidth: '420px', margin: '48px auto 0', fontStyle: 'italic' }}>
                  When diverse patient perspectives converge, patterns emerge that no single voice could reveal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium progress indicator - content slides are larger, quote slides are smaller */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '28px' }}>
        {slides.map((slide, index) => {
          const isContent = slide.type === 'content';
          const isActive = activeSlide === index;
          return (
            <button
              key={slide.id}
              onClick={() => setActiveSlide(index)}
              style={{
                width: isActive ? (isContent ? '36px' : '24px') : (isContent ? '14px' : '8px'),
                height: isContent ? '14px' : '8px',
                borderRadius: isContent ? '7px' : '4px',
                border: 'none',
                backgroundColor: isActive ? slide.darkColor : (isContent ? '#e0e0e0' : '#ececec'),
                cursor: 'pointer',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isActive ? `0 2px 8px ${slide.color}80` : 'none',
                opacity: isContent ? 1 : 0.7
              }}
            />
          );
        })}
      </div>

      {/* Slide counter - show content slide number */}
      <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#bbb', fontWeight: '500' }}>
        {(() => {
          const contentSlides = slides.filter(s => s.type === 'content');
          const currentContentIndex = contentSlides.findIndex((s, i) => {
            const slideIndex = slides.findIndex(sl => sl.id === s.id);
            return activeSlide === slideIndex || activeSlide === slideIndex + 1;
          });
          return `${String(currentContentIndex + 1).padStart(2, '0')} / ${String(contentSlides.length).padStart(2, '0')}`;
        })()}
      </div>
    </section>
  );
};

/**
 * QuoteToInsightVisual - Shows how patient quotes become structured data tags
 * Visual flow: Quote (left) → Prism (center) → Tags (right)
 * Loops through 5 quotes with sequential highlight → flow → tag animations
 */
const QuoteToInsightVisual = () => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(-1);
  const [flowingHighlightIndex, setFlowingHighlightIndex] = useState(-1);
  const [visibleTags, setVisibleTags] = useState([]);
  const [quoteVisible, setQuoteVisible] = useState(false);
  const [prismGlowing, setPrismGlowing] = useState(false);

  const quotes = [
    {
      text: "I tried to see a specialist to get a second opinion but there were none in my area",
      highlights: [
        { phrase: "second opinion", tag: "Diagnosis", color: "#7B5EA7" },
        { phrase: "none in my area", tag: "Access", color: "#D4796A" }
      ]
    },
    {
      text: "The side effects were manageable at first but then they started affecting my daily life",
      highlights: [
        { phrase: "side effects", tag: "Tolerability", color: "#2473bb" },
        { phrase: "daily life", tag: "Quality of Life", color: "#7FAFB2" }
      ]
    },
    {
      text: "I had to wait three months for insurance approval before I could start treatment",
      highlights: [
        { phrase: "insurance approval", tag: "Payer Coverage", color: "#C9A227" },
        { phrase: "start treatment", tag: "Time to Treatment", color: "#7FAFB2" }
      ]
    },
    {
      text: "My doctor explained everything clearly but the paperwork was overwhelming",
      highlights: [
        { phrase: "explained everything", tag: "HCP Communication", color: "#7B5EA7" },
        { phrase: "paperwork was overwhelming", tag: "Patient Burden", color: "#D4796A" }
      ]
    },
    {
      text: "The medication works well but I struggle to remember taking it twice a day",
      highlights: [
        { phrase: "works well", tag: "Efficacy", color: "#7FAFB2" },
        { phrase: "twice a day", tag: "Adherence", color: "#2473bb" }
      ]
    }
  ];

  const currentQuote = quotes[currentQuoteIndex];

  // Build regex pattern for current quote's highlights
  const highlightPattern = currentQuote.highlights.map(h => h.phrase).join('|');
  const regex = new RegExp(`(${highlightPattern})`, 'g');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [isVisible]);

  // Main animation loop
  useEffect(() => {
    if (!isVisible) return;

    let timeouts = [];

    const runAnimation = () => {
      // Reset state for new quote
      setActiveHighlightIndex(-1);
      setFlowingHighlightIndex(-1);
      setVisibleTags([]);
      setPrismGlowing(false);

      // Phase 1: Show quote
      setQuoteVisible(true);

      const highlights = currentQuote.highlights;
      let delay = 800; // Initial delay after quote appears

      // Animate each highlight sequentially
      highlights.forEach((highlight, index) => {
        // Highlight the phrase
        timeouts.push(setTimeout(() => {
          setActiveHighlightIndex(index);
        }, delay));
        delay += 1000;

        // Flow to prism
        timeouts.push(setTimeout(() => {
          setFlowingHighlightIndex(index);
          setPrismGlowing(true);
        }, delay));
        delay += 600;

        // Show tag
        timeouts.push(setTimeout(() => {
          setVisibleTags(prev => [...prev, index]);
          setFlowingHighlightIndex(-1);
        }, delay));
        delay += 800;
      });

      // Hold for a moment, then transition to next quote
      timeouts.push(setTimeout(() => {
        setQuoteVisible(false);
        setPrismGlowing(false);
      }, delay + 1500));

      timeouts.push(setTimeout(() => {
        setCurrentQuoteIndex(prev => (prev + 1) % quotes.length);
      }, delay + 2000));
    };

    runAnimation();

    return () => {
      timeouts.forEach(t => clearTimeout(t));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, currentQuoteIndex]);

  return (
    <div
      ref={containerRef}
      style={{
        margin: '48px 0',
        padding: '40px',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #F9F7F4 0%, #fff 100%)',
        border: '1px solid rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '220px'
      }}
    >
      <style>{`
        @keyframes quoteFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes quoteFadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-10px); }
        }
        @keyframes highlightGrow {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes flowToPrism {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(80px) scale(0.3); }
        }
        @keyframes prismPulse {
          0%, 100% { filter: drop-shadow(0 0 15px rgba(247, 192, 138, 0.2)); }
          50% { filter: drop-shadow(0 0 35px rgba(247, 192, 138, 0.5)); }
        }
        @keyframes tagPop {
          0% { opacity: 0; transform: translateX(-30px) scale(0.5); }
          60% { transform: translateX(5px) scale(1.05); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes tagFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 120px 1fr',
        gap: '32px',
        alignItems: 'center',
        minHeight: '180px'
      }}>
        {/* LEFT: Patient Quote */}
        <div style={{
          opacity: quoteVisible ? 1 : 0,
          transform: quoteVisible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.5s ease',
          position: 'relative'
        }}>
          <div style={{
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            color: '#999',
            marginBottom: '12px',
            fontWeight: '500'
          }}>Patient Quote</div>

          <div style={{
            position: 'relative',
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: '16px',
            lineHeight: '1.6',
            color: '#444',
            fontStyle: 'italic',
            paddingLeft: '20px',
            minWidth: '300px'
          }}>
            <span style={{ color: '#ccc', fontSize: '28px', position: 'absolute', left: '0', top: '-8px' }}>"</span>
            {currentQuote.text.split(regex).map((part, i) => {
              const highlightIndex = currentQuote.highlights.findIndex(h => h.phrase === part);
              if (highlightIndex !== -1) {
                const highlight = currentQuote.highlights[highlightIndex];
                const isActive = activeHighlightIndex >= highlightIndex;
                const isFlowing = flowingHighlightIndex === highlightIndex;
                return (
                  <span
                    key={`${currentQuoteIndex}-${i}`}
                    style={{
                      position: 'relative',
                      display: 'inline'
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      left: '-2px',
                      right: '-2px',
                      bottom: '2px',
                      height: '50%',
                      background: highlight.color,
                      opacity: isActive ? 0.55 : 0,
                      borderRadius: '2px',
                      transformOrigin: 'left',
                      animation: isActive && activeHighlightIndex === highlightIndex ? 'highlightGrow 0.4s ease-out forwards' : 'none',
                      transform: isActive ? 'scaleX(1)' : 'scaleX(0)'
                    }} />
                    <span style={{
                      position: 'relative',
                      fontWeight: isActive ? '600' : '400',
                      color: isActive ? highlight.color : '#444',
                      transition: 'all 0.3s ease'
                    }}>{part}</span>
                    {/* Flowing particle */}
                    {isFlowing && (
                      <span style={{
                        position: 'absolute',
                        right: '-8px',
                        top: '50%',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: highlight.color,
                        boxShadow: `0 0 10px ${highlight.color}`,
                        animation: 'flowToPrism 0.6s ease-in forwards'
                      }} />
                    )}
                  </span>
                );
              }
              return <span key={`${currentQuoteIndex}-${i}`}>{part}</span>;
            })}
            <span style={{ color: '#ccc', fontSize: '28px' }}>"</span>
          </div>
        </div>

        {/* CENTER: Prism */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <svg
            width="120"
            height="160"
            viewBox="0 0 120 160"
            style={{
              animation: prismGlowing ? 'prismPulse 0.8s ease-in-out' : 'none',
              transition: 'filter 0.3s ease',
              filter: prismGlowing ? 'drop-shadow(0 0 25px rgba(247, 192, 138, 0.4))' : 'drop-shadow(0 0 10px rgba(0,0,0,0.1))'
            }}
          >
            <defs>
              <linearGradient id="quoteprism-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                <stop offset="50%" stopColor="rgba(250,245,240,0.9)" />
                <stop offset="100%" stopColor="rgba(240,235,230,0.85)" />
              </linearGradient>
              <linearGradient id="quoteprism-edge" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(212,121,106,0.4)" />
                <stop offset="50%" stopColor="rgba(201,162,39,0.3)" />
                <stop offset="100%" stopColor="rgba(123,94,167,0.4)" />
              </linearGradient>
            </defs>

            {/* Main prism shape */}
            <polygon
              points="60,10 110,140 10,140"
              fill="url(#quoteprism-gradient)"
              stroke="url(#quoteprism-edge)"
              strokeWidth="2"
            />

            {/* Inner facet lines */}
            <line x1="60" y1="10" x2="40" y2="140" stroke="rgba(212,121,106,0.15)" strokeWidth="1" />
            <line x1="60" y1="10" x2="80" y2="140" stroke="rgba(123,94,167,0.15)" strokeWidth="1" />

            {/* Reflection highlight */}
            <polygon
              points="60,25 75,70 55,90 45,60"
              fill="rgba(255,255,255,0.4)"
            />
          </svg>

          {/* "Transform" label */}
          <div style={{
            position: 'absolute',
            bottom: '-8px',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: '#aaa',
            fontWeight: '500'
          }}>Real Patients™</div>
        </div>

        {/* RIGHT: Tags */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'flex-start',
          minHeight: '140px'
        }}>
          <div style={{
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            color: '#999',
            marginBottom: '4px',
            fontWeight: '500',
            opacity: visibleTags.length > 0 ? 1 : 0.3,
            transition: 'opacity 0.3s ease'
          }}>Structured Data</div>

          {currentQuote.highlights.map((h, i) => (
            <div
              key={`${currentQuoteIndex}-tag-${i}`}
              style={{
                opacity: visibleTags.includes(i) ? 1 : 0,
                animation: visibleTags.includes(i) ? `tagPop 0.5s ease-out forwards, tagFloat 3s ease-in-out infinite ${i * 0.3}s` : 'none'
              }}
            >
              <div style={{
                padding: '10px 18px',
                borderRadius: '24px',
                background: `linear-gradient(135deg, ${h.color}15 0%, ${h.color}08 100%)`,
                border: `1.5px solid ${h.color}40`,
                color: h.color,
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: `0 4px 12px ${h.color}15`,
                whiteSpace: 'nowrap'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: h.color,
                  opacity: 0.8
                }} />
                {h.tag}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quote indicator dots */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '24px'
      }}>
        {quotes.map((_, i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: i === currentQuoteIndex ? '#F7C08A' : '#ddd',
              transition: 'all 0.3s ease',
              transform: i === currentQuoteIndex ? 'scale(1.2)' : 'scale(1)'
            }}
          />
        ))}
      </div>

      {/* Mobile responsive */}
      <style>{`
        @media (max-width: 768px) {
          .quote-to-insight-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * PrismFragment - A single decorative prism shape
 * Can be positioned absolutely within a relative container
 */
const PrismFragment = ({
  top, right, bottom, left,
  width = 60,
  height = 40,
  colorFrom = '#FFE4D9',
  colorTo = '#FFCBB8',
  rotate = 15,
  shape = 'parallelogram-right',
  opacity = 0.5,
  gradientDir = '135deg'
}) => {
  const shapeMap = {
    'rectangle': 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
    'parallelogram-right': 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)',
    'parallelogram-left': 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)',
    'trapezoid-down': 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)',
    'trapezoid-up': 'polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)',
    'rhomboid': 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)',
    'chevron': 'polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%)',
    'pentagon': 'polygon(50% 0%, 100% 35%, 80% 100%, 20% 100%, 0% 35%)',
  };

  return (
    <div
      style={{
        position: 'absolute',
        top, right, bottom, left,
        width: `${width}px`,
        height: `${height}px`,
        background: `linear-gradient(${gradientDir}, ${colorFrom} 0%, ${colorTo} 100%)`,
        opacity,
        transform: `rotate(${rotate}deg)`,
        clipPath: shapeMap[shape] || shapeMap['parallelogram-right'],
        zIndex: 0
      }}
    />
  );
};

/**
 * PrismHighlight - Text highlighter using the prism fragment style
 * Usage: <PrismHighlight color="#FFB4A0">highlighted text</PrismHighlight>
 */
// eslint-disable-next-line no-unused-vars
const PrismHighlight = ({ children, color = '#FFB4A0', colorTo, rotation = -2, shape = 'parallelogram-right' }) => {
  const shapeMap = {
    'rectangle': 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
    'parallelogram-right': 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)',
    'parallelogram-left': 'polygon(0% 0%, 92% 0%, 100% 100%, 8% 100%)',
    'trapezoid': 'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)',
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={{
          position: 'absolute',
          left: '-4%',
          right: '-4%',
          bottom: '0',
          height: '40%',
          background: colorTo
            ? `linear-gradient(90deg, ${color} 0%, ${colorTo} 100%)`
            : color,
          opacity: 0.6,
          transform: `rotate(${rotation}deg)`,
          clipPath: shapeMap[shape] || shapeMap['parallelogram-right'],
          zIndex: -1
        }}
      />
      <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
    </span>
  );
};

/**
 * PrismCard - Card with decorative prism fragments
 */
// eslint-disable-next-line no-unused-vars
const PrismCard = ({ children, variant = 1 }) => {
  const fragmentSets = [
    // Variant 1 - top right, bottom left
    [
      { top: '-20px', right: '-15px', width: 70, height: 45, colorFrom: '#FFE4D9', colorTo: '#FFCBB8', rotate: 25, shape: 'parallelogram-right' },
      { bottom: '-25px', left: '-10px', width: 55, height: 75, colorFrom: '#FFCBB8', colorTo: '#FFB4A0', rotate: -18, shape: 'trapezoid-down' },
    ],
    // Variant 2 - multiple small pieces
    [
      { top: '-15px', right: '20%', width: 50, height: 35, colorFrom: '#FFE4D9', colorTo: '#FFCBB8', rotate: 20, shape: 'parallelogram-left' },
      { bottom: '-20px', right: '-15px', width: 65, height: 45, colorFrom: '#FFB4A0', colorTo: '#F7C08A', rotate: 15, shape: 'rhomboid' },
      { top: '40%', left: '-20px', width: 45, height: 60, colorFrom: '#FFCBB8', colorTo: '#FFB4A0', rotate: -25, shape: 'trapezoid-up' },
    ],
    // Variant 3 - diagonal cluster
    [
      { top: '-25px', left: '-15px', width: 60, height: 40, colorFrom: '#FFE4D9', colorTo: '#FFCBB8', rotate: -15, shape: 'parallelogram-right' },
      { bottom: '-20px', right: '-20px', width: 80, height: 55, colorFrom: '#F7C08A', colorTo: '#F7C08A', rotate: 22, shape: 'trapezoid-down' },
      { top: '30%', right: '-25px', width: 40, height: 70, colorFrom: '#FFB4A0', colorTo: '#F7C08A', rotate: 35, shape: 'parallelogram-left' },
    ],
  ];

  const fragments = fragmentSets[(variant - 1) % fragmentSets.length];

  return (
    <div style={{ position: 'relative', padding: '32px' }}>
      {fragments.map((frag, i) => (
        <PrismFragment key={i} {...frag} opacity={0.5} />
      ))}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

/**
 * InteractiveTimeline - Winding path timeline inspired by Going.com
 * Large years as primary visual, cards along a curved dotted path
 */
const InteractiveTimeline = ({ milestones, WatercolorHighlight }) => {
  const [visibleCards, setVisibleCards] = useState([]);
  const timelineRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.dataset.index);
          if (entry.isIntersecting) {
            setVisibleCards((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-20px' }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={timelineRef}
      style={{
        padding: '60px 40px 80px',
        backgroundColor: '#faf8f6',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10
      }}
    >
      {/* Background prism decorations */}
      <PrismFragment top="5%" left="3%" width={100} height={70} colorFrom="#FFE4D9" colorTo="#FFCBB8" rotate={-12} shape="parallelogram-right" opacity={0.12} />
      <PrismFragment top="40%" right="2%" width={80} height={55} colorFrom="#FFCBB8" colorTo="#FFB4A0" rotate={18} shape="trapezoid-up" opacity={0.1} />
      <PrismFragment bottom="30%" left="2%" width={70} height={90} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={-20} shape="rhomboid" opacity={0.08} />
      <PrismFragment bottom="10%" right="5%" width={90} height={60} colorFrom="#FBF4BC" colorTo="#FBF4BC" rotate={15} shape="parallelogram-left" opacity={0.1} />

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '600',
              color: '#111111',
              marginBottom: '16px'
            }}
          >
            Evolution of <WatercolorHighlight gradient="rgba(127, 175, 178, 0.6), rgba(200, 224, 220, 0.5)">Real Patients™</WatercolorHighlight>
          </h2>
          <p style={{ fontSize: '18px', color: '#4A4A4A', maxWidth: '600px', margin: '0 auto' }}>
            Ten years of building toward what healthcare needs now.
          </p>
        </div>

        {/* Winding path timeline */}
        <div style={{ position: 'relative' }}>
          {/* SVG winding dotted path */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0
            }}
            viewBox="0 0 800 1400"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="timelineHighlighter" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F7C08A" stopOpacity="0.6" />
                <stop offset="30%" stopColor="#F7C08A" stopOpacity="0.5" />
                <stop offset="60%" stopColor="#FBF4BC" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#7FAFB2" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            <path
              d="M 400 0
                 C 200 100, 200 150, 400 200
                 C 600 250, 600 350, 400 400
                 C 200 450, 200 550, 400 600
                 C 600 650, 600 750, 400 800
                 C 200 850, 200 950, 400 1000
                 C 600 1050, 600 1150, 400 1200
                 C 200 1250, 200 1350, 400 1400"
              fill="none"
              stroke="url(#timelineHighlighter)"
              strokeWidth="8"
              strokeLinecap="round"
            />
          </svg>

          {/* Timeline milestones */}
          {milestones.map((milestone, index) => {
            const isLeft = index % 2 === 0;
            const isVisible = visibleCards.includes(index);

            return (
              <div
                key={milestone.year}
                ref={(el) => (cardRefs.current[index] = el)}
                data-index={index}
                style={{
                  display: 'flex',
                  flexDirection: isLeft ? 'row' : 'row-reverse',
                  alignItems: 'center',
                  gap: '40px',
                  marginBottom: index === milestones.length - 1 ? '0' : '80px',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {/* Year */}
                <div
                  style={{
                    flex: '0 0 45%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isLeft ? 'flex-start' : 'flex-end',
                    gap: '16px',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible
                      ? 'translateY(0)'
                      : isLeft ? 'translateX(-30px)' : 'translateX(30px)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {/* Year number - compact */}
                  <span
                    style={{
                      fontFamily: '"Source Serif Pro", Georgia, serif',
                      fontSize: 'clamp(32px, 5vw, 50px)',
                      fontWeight: '600',
                      color: milestone.color,
                      lineHeight: 1,
                      opacity: 0.9
                    }}
                  >
                    {milestone.year}
                  </span>
                </div>

                {/* Content card */}
                <div
                  style={{
                    flex: '0 0 45%',
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    padding: '28px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: milestone.featured
                      ? `2px solid ${milestone.color}30`
                      : '1px solid rgba(0,0,0,0.06)',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible
                      ? 'translateY(0)'
                      : isLeft ? 'translateX(30px)' : 'translateX(-30px)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: '0.15s'
                  }}
                >
                  {/* Headline */}
                  <h3
                    style={{
                      fontFamily: '"Source Serif Pro", Georgia, serif',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#111111',
                      marginTop: 0,
                      marginBottom: '10px',
                      lineHeight: 1.3
                    }}
                  >
                    {milestone.headline}
                  </h3>

                  {/* Description - shorter */}
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#4A4A4A',
                      lineHeight: '1.6',
                      margin: 0
                    }}
                  >
                    {milestone.shortDescription || milestone.description.split('.').slice(0, 2).join('.') + '.'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * PartnersLanding - Story-driven B2B landing page for pharma/biotech partners
 * Tells the Real Patients origin story, evolution, and PRISM interest collection
 */
export const PartnersLanding = ({ onBackClick, onContactClick, onOurStoryClick, onPrivacyClick, onTermsClick, onSignUpClick, bannerOffset = 0, footerProps = {} }) => {
  const [selectedPrisms, setSelectedPrisms] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    role: '',
    companySize: '',
    timeline: '',
    primaryInterest: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Trust section ref - fragments wait until Trust section passes
  const trustSectionRef = useRef(null);
  const [trustSectionPassed, setTrustSectionPassed] = useState(false);

  // Track when Trust section passes middle of viewport
  useEffect(() => {
    const handleScroll = () => {
      if (!trustSectionRef.current || trustSectionPassed) return;
      const rect = trustSectionRef.current.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;
      // Trust section has passed when its bottom is above viewport middle
      if (rect.bottom < viewportMiddle) {
        setTrustSectionPassed(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trustSectionPassed]);

  // Comparison rows scroll reveal animation
  // eslint-disable-next-line no-unused-vars
  const comparisonRef = useRef(null);
  const [visibleComparisonRows, setVisibleComparisonRows] = useState([]);

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.index, 10);
          setVisibleComparisonRows((prev) => {
            if (!prev.includes(index)) {
              return [...prev, index];
            }
            return prev;
          });
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.5,
      rootMargin: '0px 0px -100px 0px'
    });

    const rows = document.querySelectorAll('.comparison-row-animate');
    rows.forEach((row) => observer.observe(row));

    return () => observer.disconnect();
  }, []);

  // Why This Matters Now - sequential reveal animation
  const [visibleWhyMattersItems, setVisibleWhyMattersItems] = useState([]);

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.index, 10);
          // Stagger the reveals with setTimeout
          setTimeout(() => {
            setVisibleWhyMattersItems((prev) => {
              if (!prev.includes(index)) {
                return [...prev, index];
              }
              return prev;
            });
          }, index * 400); // 400ms delay between each item
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3,
      rootMargin: '0px 0px -50px 0px'
    });

    const items = document.querySelectorAll('.why-matters-reveal');
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  // Therapeutic areas for PRISM waitlist - with SVG icons
  const therapeuticAreas = [
    { id: 'oncology', name: 'Oncology', color: '#F7C08A' },
    { id: 'rare-disease', name: 'Rare Disease', color: '#FABEC0' },
    { id: 'autoimmune', name: 'Autoimmune', color: '#F7C08A' },
    { id: 'mental-health', name: 'Mental Health', color: '#7FAFB2' },
    { id: 'cardiovascular', name: 'Cardiovascular', color: '#F7C08A' },
    { id: 'respiratory', name: 'Respiratory', color: '#F7C08A' },
    { id: 'dermatology', name: 'Dermatology', color: '#FBF4BC' },
    { id: 'gastro', name: 'Gastroenterology', color: '#7FAFB2' },
    { id: 'womens-health', name: "Women's Health", color: '#FABEC0' },
    { id: 'endocrine', name: 'Endocrine', color: '#FBF4BC' },
    { id: 'infectious', name: 'Infectious Disease', color: '#F7C08A' },
    { id: 'other', name: 'Other', color: '#999' }
  ];

  // Evolution timeline milestones
  const milestones = [
    {
      year: '2016',
      headline: 'Expanding access to real patients',
      description: 'We launched as Savvy Cooperative® to make it easy for organizations to engage real, diverse patients for insight and testing—removing barriers so there was no excuse not to listen',
      color: '#F7C08A'
    },
    {
      year: '2018',
      headline: 'Elevating the quality of patient research',
      description: 'As demand grew, it became clear that access alone wasn\'t enough. We expanded to conduct patient-centered research in-house—ensuring rigor, neutrality, and fit-for-purpose methods',
      color: '#F7C08A'
    },
    {
      year: '2020',
      headline: 'Making insight continuous, not episodic',
      description: 'To help teams move faster and stay connected to patients, we introduced a subscription-based model—reducing friction and enabling ongoing learning through continuous engagement',
      color: '#E8D477'
    },
    {
      year: '2022',
      headline: 'Designing for reuse and respect',
      description: 'Patients were asked the same questions repeatedly. We began building Foundational Patient Experience—a way to capture insight once, and update and use it responsibly over time',
      color: '#7FAFB2'
    },
    {
      year: '2024',
      headline: 'Building for AI from day one',
      description: 'Legacy data wasn\'t fit for modern analytics. We launched early access to FPE through PRISM, designing the data from the ground up to be structured, contextual, and AI-ready',
      color: '#F7C08A'
    },
    {
      year: '2026',
      headline: 'Real Patients™',
      description: 'We rebranded to reflect what the work had always been about: ensuring that as AI and automation shape healthcare, real patient experience remains at the center',
      color: '#7FAFB2',
      featured: true
    }
  ];

  // Full Stack Pillars with detailed content and illustrations
  const fullStackPillars = [
    {
      number: '01',
      label: 'Community',
      name: 'Real patient relationships—not panels.',
      color: '#EBA09B',
      description: 'A continuously engaged, identity-verified patient community spanning 2,500+ conditions. Organized into indication-specific cohorts and built for long-term participation—not transactional research.',
      keyPoints: ['2,500+ conditions', 'Identity-verified', 'Long-term engagement', 'Indication-specific'],
      illustration: (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
          {/* Community illustration - people connected */}
          <circle cx="80" cy="50" r="20" fill="#fff" stroke="#EBA09B" strokeWidth="3"/>
          <circle cx="80" cy="50" r="8" fill="#EBA09B"/>
          <path d="M60 90 Q80 70 100 90" stroke="#EBA09B" strokeWidth="3" fill="none"/>
          <circle cx="45" cy="95" r="16" fill="#fff" stroke="#EBA09B" strokeWidth="2.5"/>
          <circle cx="45" cy="95" r="6" fill="#EBA09B" opacity="0.7"/>
          <circle cx="115" cy="95" r="16" fill="#fff" stroke="#EBA09B" strokeWidth="2.5"/>
          <circle cx="115" cy="95" r="6" fill="#EBA09B" opacity="0.7"/>
          <circle cx="35" cy="130" r="12" fill="#fff" stroke="#EBA09B" strokeWidth="2"/>
          <circle cx="65" cy="135" r="12" fill="#fff" stroke="#EBA09B" strokeWidth="2"/>
          <circle cx="95" cy="135" r="12" fill="#fff" stroke="#EBA09B" strokeWidth="2"/>
          <circle cx="125" cy="130" r="12" fill="#fff" stroke="#EBA09B" strokeWidth="2"/>
          <path d="M45 111 L35 118 M45 111 L65 123 M115 111 L125 118 M115 111 L95 123" stroke="#EBA09B" strokeWidth="2" strokeDasharray="3 2"/>
        </svg>
      )
    },
    {
      number: '02',
      label: 'Data',
      name: 'Foundational Patient Experience™ (FPE)',
      color: '#F5B9A5',
      description: 'Longitudinal, regulatory-grade patient experience data capturing the full spectrum of life with a condition—beyond clinical touchpoints. Structured, demographically tagged, and designed to reduce patient burden while increasing insight value.',
      keyPoints: ['Regulatory-grade', 'Longitudinal', 'Demographically tagged', 'Reduced burden'],
      illustration: (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
          {/* Data layers - stacked parallelograms */}
          <path d="M30 120 L50 100 L130 100 L110 120 Z" fill="#F5B9A5" stroke="#fff" strokeWidth="2"/>
          <path d="M35 105 L55 85 L125 85 L105 105 Z" fill="#F5B9A5" opacity="0.85" stroke="#fff" strokeWidth="2"/>
          <path d="M40 90 L60 70 L120 70 L100 90 Z" fill="#F5B9A5" opacity="0.7" stroke="#fff" strokeWidth="2"/>
          <path d="M45 75 L65 55 L115 55 L95 75 Z" fill="#F5B9A5" opacity="0.55" stroke="#fff" strokeWidth="2"/>
          <path d="M50 60 L70 40 L110 40 L90 60 Z" fill="#F5B9A5" opacity="0.4" stroke="#fff" strokeWidth="2"/>
          {/* Data points */}
          <circle cx="70" cy="110" r="4" fill="#fff"/>
          <circle cx="90" cy="110" r="4" fill="#fff"/>
          <circle cx="110" cy="110" r="4" fill="#fff"/>
          <rect x="65" y="92" width="8" height="4" fill="#fff" rx="1"/>
          <rect x="85" y="92" width="12" height="4" fill="#fff" rx="1"/>
        </svg>
      )
    },
    {
      number: '03',
      label: 'Insights',
      name: 'PRISM™ Personalized Intelligence',
      color: '#F7C08A',
      description: 'A dynamic way to explore, analyze, and work with patient experience data. Searchable by demographic, domain, and theme—built for cross-functional use, analytics, and AI-enabled applications.',
      keyPoints: ['Searchable', 'Cross-functional', 'AI-enabled', 'Dynamic exploration'],
      illustration: (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
          {/* Lightbulb / insight prism */}
          <path d="M80 25 L120 95 L40 95 Z" fill="#F7C08A" stroke="#fff" strokeWidth="3"/>
          <path d="M80 45 L100 85 L60 85 Z" fill="#fff" opacity="0.5"/>
          {/* Light rays */}
          <path d="M80 20 L80 8" stroke="#F7C08A" strokeWidth="3" strokeLinecap="round"/>
          <path d="M55 30 L45 20" stroke="#F7C08A" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M105 30 L115 20" stroke="#F7C08A" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M35 55 L25 50" stroke="#F7C08A" strokeWidth="2" strokeLinecap="round"/>
          <path d="M125 55 L135 50" stroke="#F7C08A" strokeWidth="2" strokeLinecap="round"/>
          {/* Base */}
          <rect x="65" y="98" width="30" height="8" fill="#F7C08A" stroke="#fff" strokeWidth="2" rx="2"/>
          <rect x="70" y="108" width="20" height="6" fill="#F7C08A" stroke="#fff" strokeWidth="2" rx="2"/>
          {/* Magnifying detail */}
          <circle cx="115" cy="120" r="18" fill="none" stroke="#F7C08A" strokeWidth="3"/>
          <line x1="128" y1="133" x2="140" y2="145" stroke="#F7C08A" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      number: '04',
      label: 'Activation',
      name: 'REACT™ Custom Research',
      color: '#FBF4BC',
      description: 'On-demand qualitative and mixed-method research layered on top of existing patient context. Go deeper on organization-specific questions—without repeating recruitment or starting from zero.',
      keyPoints: ['On-demand', 'Mixed-method', 'No repeat recruitment', 'Custom research'],
      illustration: (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
          {/* Speed/activation - lightning in prism */}
          <path d="M40 130 L80 30 L120 130 Z" fill="none" stroke="#FBF4BC" strokeWidth="3"/>
          {/* Lightning bolt */}
          <path d="M90 50 L70 80 L85 80 L65 115 L100 75 L82 75 L100 50 Z" fill="#FBF4BC" stroke="#fff" strokeWidth="2"/>
          {/* Speed lines */}
          <path d="M30 60 L15 60" stroke="#FBF4BC" strokeWidth="2" strokeLinecap="round"/>
          <path d="M25 80 L10 80" stroke="#FBF4BC" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M30 100 L15 100" stroke="#FBF4BC" strokeWidth="2" strokeLinecap="round"/>
          <path d="M130 60 L145 60" stroke="#FBF4BC" strokeWidth="2" strokeLinecap="round"/>
          <path d="M135 80 L150 80" stroke="#FBF4BC" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M130 100 L145 100" stroke="#FBF4BC" strokeWidth="2" strokeLinecap="round"/>
          {/* Pulse circles */}
          <circle cx="80" cy="80" r="45" fill="none" stroke="#FBF4BC" strokeWidth="1.5" opacity="0.3"/>
          <circle cx="80" cy="80" r="55" fill="none" stroke="#FBF4BC" strokeWidth="1" opacity="0.2"/>
        </svg>
      )
    },
    {
      number: '05',
      label: 'Implementation',
      name: 'Turning insight into action—across teams.',
      color: '#7FAFB2',
      description: 'Strategic support to translate patient insight into decisions, alignment, and execution. From stakeholder-ready narratives to integration with analytics, BI, and AI workflows.',
      keyPoints: ['Strategic support', 'Stakeholder-ready', 'Analytics integration', 'AI workflows'],
      illustration: (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
          {/* Target / implementation */}
          <circle cx="80" cy="80" r="50" fill="none" stroke="#7FAFB2" strokeWidth="3"/>
          <circle cx="80" cy="80" r="35" fill="none" stroke="#7FAFB2" strokeWidth="2.5" opacity="0.7"/>
          <circle cx="80" cy="80" r="20" fill="none" stroke="#7FAFB2" strokeWidth="2" opacity="0.5"/>
          <circle cx="80" cy="80" r="8" fill="#7FAFB2"/>
          {/* Arrow hitting target */}
          <path d="M130 30 L90 70" stroke="#7FAFB2" strokeWidth="3" strokeLinecap="round"/>
          <path d="M130 30 L120 32 L128 40 Z" fill="#7FAFB2"/>
          {/* Integration lines */}
          <path d="M80 25 L80 10" stroke="#7FAFB2" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3"/>
          <path d="M80 150 L80 135" stroke="#7FAFB2" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3"/>
          <path d="M25 80 L10 80" stroke="#7FAFB2" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3"/>
          <path d="M150 80 L135 80" stroke="#7FAFB2" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3"/>
          {/* Checkmark */}
          <path d="M70 82 L78 90 L95 70" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  const togglePrism = (id) => {
    setSelectedPrisms(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedNames = selectedPrisms.map(id =>
      therapeuticAreas.find(t => t.id === id)?.name
    ).filter(Boolean).join('; ');

    // Capture UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || ''
    };

    // Paperform submission
    // To enable: Create a Paperform, map fields, add submission URL to env
    const PAPERFORM_URL = process.env.REACT_APP_PAPERFORM_URL;

    const submissionData = {
      name: formData.name,
      company: formData.company,
      email: formData.email,
      role: formData.role,
      company_size: formData.companySize,
      timeline: formData.timeline,
      primary_interest: formData.primaryInterest,
      therapeutic_areas: selectedNames,
      message: formData.message,
      ...utmParams
    };

    setFormError(false);
    setSubmitting(true);
    try {
      if (!PAPERFORM_URL) throw new Error('Missing submission endpoint');
      // Send as URL-encoded form data (Zapier prefers this)
      const formBody = new URLSearchParams(submissionData).toString();
      const res = await fetch(PAPERFORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });
      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      // Only show success once the lead is actually delivered
      setFormSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Watercolor text highlight component - scroll-triggered animation
  const WatercolorHighlight = ({ children, gradient = 'rgba(247, 192, 138, 0.5), rgba(247, 192, 138, 0.4)' }) => {
    const highlightRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        {
          threshold: 1.0,
          rootMargin: '-15% 0px -15% 0px'
        }
      );

      if (highlightRef.current) observer.observe(highlightRef.current);
      return () => observer.disconnect();
    }, []);

    return (
      <span ref={highlightRef} style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
        <span
          style={{
            position: 'absolute',
            left: '-2%',
            right: '-4%',
            bottom: '5%',
            height: '40%',
            background: `linear-gradient(90deg, ${gradient})`,
            transform: `skewX(-2deg) scaleX(${isVisible ? 1 : 0})`,
            transformOrigin: 'left',
            zIndex: 0,
            borderRadius: '3px 6px 5px 3px',
            transition: 'transform 1.05s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
      </span>
    );
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#111111',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <Header
        onLogoClick={onBackClick}
        onSignUpClick={onSignUpClick}
        onPartnersClick={null}
        onAboutClick={onOurStoryClick}
        bannerOffset={bannerOffset}
      />

      {/* ========== SECTION 1: STORY HERO ========== */}
      <section
        className="partners-hero"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #faf8f6 0%, #fff 100%)',
          position: 'relative',
          paddingTop: '160px',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Floating prism decorations */}
        <PrismFragment top="15%" left="8%" width={100} height={70} colorFrom="#FFE4D9" colorTo="#FFCBB8" rotate={-12} shape="parallelogram-left" opacity={0.25} />
        <PrismFragment top="25%" right="10%" width={80} height={55} colorFrom="#FFCBB8" colorTo="#FFB4A0" rotate={18} shape="trapezoid-up" opacity={0.2} />
        <PrismFragment bottom="20%" left="5%" width={70} height={90} colorFrom="#FFB4A0" colorTo="#F7C08A" rotate={25} shape="rhomboid" opacity={0.18} />
        <PrismFragment bottom="30%" right="8%" width={90} height={60} colorFrom="#F7C08A" colorTo="#F7C08A" rotate={-20} shape="trapezoid-down" opacity={0.15} />
        <PrismFragment top="45%" left="3%" width={95} height={32} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={30} shape="parallelogram-right" opacity={0.15} />
        <PrismFragment top="60%" right="5%" width={105} height={35} colorFrom="#FFE4D9" colorTo="#FFB4A0" rotate={-28} shape="rectangle" opacity={0.12} />

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '60px 40px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(36px, 5vw, 56px)',
              fontWeight: '600',
              color: '#111111',
              lineHeight: '1.2',
              marginBottom: '32px'
            }}
          >
            We took an <WatercolorHighlight gradient="rgba(247, 192, 138, 0.45), rgba(247, 192, 138, 0.35)">outdated model</WatercolorHighlight><br />of patient research—and <WatercolorHighlight gradient="rgba(127, 175, 178, 0.45), rgba(127, 175, 178, 0.35)">rebuilt it</WatercolorHighlight> for today.
          </h1>
          <p
            style={{
              fontSize: '20px',
              color: '#4A4A4A',
              lineHeight: '1.7',
              maxWidth: '880px',
              margin: '0 auto 48px'
            }}
          >
            <span style={{ fontWeight: '600', color: '#111111' }}>Real Patients™</span> is a full-stack patient engagement partner, trusted for a decade to capture,<br />structure, and activate real patient experience—ethically, longitudinally, and at scale.
          </p>

          {/* CTA Button */}
          <button
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#fff',
              backgroundColor: '#111111',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => document.getElementById('express-interest')?.scrollIntoView({ behavior: 'smooth' })}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#3a3a39';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#111111';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Explore partnership by therapeutic area
            <span style={{ marginLeft: '4px' }}>→</span>
          </button>
        </div>
      </section>

      <BrushstrokeDivider bgColor="#fff" />

      {/* ========== TRUSTED PARTNERS - Logo Marquee ========== */}
      <section
        ref={trustSectionRef}
        style={{
          padding: '60px 0',
          backgroundColor: '#f9f9f9',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 20
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px', padding: '0 40px', maxWidth: '800px', margin: '0 auto 40px' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: '600',
              color: '#111111',
              margin: '0 0 16px 0'
            }}
          >
            <WatercolorHighlight gradient="rgba(251, 244, 188, 1), rgba(251, 244, 188, 0.85)">Trusted</WatercolorHighlight> by leading pharma, biotech,<br />and healthcare innovators
          </h2>
          <p
            style={{
              fontSize: '17px',
              color: '#4A4A4A',
              lineHeight: '1.7',
              margin: 0,
              fontStyle: 'italic'
            }}
          >
            For a decade, teams across pharma, biotech, medtech, and digital health have partnered with<br />Real Patients™ to deliver patient insight with rigor, integrity, and real-world relevance.
          </p>
        </div>

        {/* CSS for marquee + hover interactions */}
        <style>
          {`
            @keyframes marqueeLeft {
              0% { transform: translateX(0); }
              100% { transform: translateX(-25%); }
            }
            @keyframes marqueeRight {
              0% { transform: translateX(-25%); }
              100% { transform: translateX(0); }
            }
            .partner-marquee-row:hover .partner-marquee-track {
              animation-play-state: paused;
            }
            .partner-logo-item {
              position: relative;
              flex-shrink: 0;
              margin-right: 80px;
              cursor: default;
              text-align: center;
            }
            .partner-logo-item img {
              filter: grayscale(1);
              opacity: 0.7;
              transition: filter 0.3s ease, opacity 0.3s ease;
            }
            .partner-logo-item:hover img {
              filter: grayscale(0);
              opacity: 1;
            }
            .partner-logo-tag {
              display: block;
              font-size: 11px;
              font-weight: 500;
              letter-spacing: 0.5px;
              text-transform: uppercase;
              color: transparent;
              margin-top: 8px;
              transition: color 0.3s ease;
              white-space: nowrap;
            }
            .partner-logo-item:hover .partner-logo-tag {
              color: #7FAFB2;
            }
          `}
        </style>

        {/* Row 1 - Scrolls Left */}
        <div className="partner-marquee-row" style={{ display: 'flex', overflow: 'hidden', marginBottom: '24px' }}>
          <div className="partner-marquee-track" style={{
            display: 'flex',
            animation: 'marqueeLeft 40s linear infinite',
            alignItems: 'flex-start',
            whiteSpace: 'nowrap'
          }}>
            {[...Array(4)].map((_, setIndex) => (
              <React.Fragment key={setIndex}>
                <div className="partner-logo-item"><img src="/logos/pfizer.svg" alt="Pfizer" style={{ height: '32px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/merck.svg" alt="Merck" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/jnj.svg" alt="Johnson & Johnson" style={{ height: '24px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/abbvie.svg" alt="AbbVie" style={{ height: '34px' }} /><span className="partner-logo-tag">Biopharma</span></div>
                <div className="partner-logo-item"><img src="/logos/astrazeneca.svg" alt="AstraZeneca" style={{ height: '36px' }} /><span className="partner-logo-tag">Biopharma</span></div>
                <div className="partner-logo-item"><img src="/logos/bms.svg" alt="Bristol-Myers Squibb" style={{ height: '34px' }} /><span className="partner-logo-tag">Biopharma</span></div>
                <div className="partner-logo-item"><img src="/logos/sanofi.svg" alt="Sanofi" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/amazon.svg" alt="Amazon" style={{ height: '28px' }} /><span className="partner-logo-tag">Technology</span></div>
                <div className="partner-logo-item"><img src="/logos/roche.svg" alt="Roche" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/lilly.svg" alt="Eli Lilly" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/novartis.svg" alt="Novartis" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/salesforce.svg" alt="Salesforce" style={{ height: '32px' }} /><span className="partner-logo-tag">Technology</span></div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Row 2 - Scrolls Right */}
        <div className="partner-marquee-row" style={{ display: 'flex', overflow: 'hidden' }}>
          <div className="partner-marquee-track" style={{
            display: 'flex',
            animation: 'marqueeRight 45s linear infinite',
            alignItems: 'flex-start',
            whiteSpace: 'nowrap'
          }}>
            {[...Array(4)].map((_, setIndex) => (
              <React.Fragment key={setIndex}>
                <div className="partner-logo-item"><img src="/logos/novonordisk.svg" alt="Novo Nordisk" style={{ height: '44px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/gsk.svg" alt="GSK" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/takeda.svg" alt="Takeda" style={{ height: '34px' }} /><span className="partner-logo-tag">Biopharma</span></div>
                <div className="partner-logo-item"><img src="/logos/gilead.svg" alt="Gilead" style={{ height: '34px' }} /><span className="partner-logo-tag">Biopharma</span></div>
                <div className="partner-logo-item"><img src="/logos/amgen.svg" alt="Amgen" style={{ height: '34px' }} /><span className="partner-logo-tag">Biotech</span></div>
                <div className="partner-logo-item"><img src="/logos/bayer.svg" alt="Bayer" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/medtronic.svg" alt="Medtronic" style={{ height: '32px' }} /><span className="partner-logo-tag">MedTech</span></div>
                <div className="partner-logo-item"><img src="/logos/bi.png" alt="Boehringer Ingelheim" style={{ height: '34px' }} /><span className="partner-logo-tag">Pharma</span></div>
                <div className="partner-logo-item"><img src="/logos/regeneron.svg" alt="Regeneron" style={{ height: '22px' }} /><span className="partner-logo-tag">Biotech</span></div>
                <div className="partner-logo-item"><img src="/logos/moderna.svg" alt="Moderna" style={{ height: '30px' }} /><span className="partner-logo-tag">Biotech</span></div>
                <div className="partner-logo-item"><img src="/logos/biogen.svg" alt="Biogen" style={{ height: '34px' }} /><span className="partner-logo-tag">Biotech</span></div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SECTION: PRISM CONVERGENCE - Real patients are multidimensional ========== */}
      <PrismConvergence trustSectionPassed={trustSectionPassed} />

      {/* ========== SECTION: A DIFFERENT WAY TO WORK WITH PATIENTS ========== */}
      <style>{`
        .different-way-section {
          padding: 100px 40px;
        }
        .living-data-section {
          padding: 100px 40px;
        }
        @media (max-width: 600px) {
          .partners-hero {
            padding-top: 140px !important;
          }
          .different-way-section {
            display: none !important;
          }
          .living-data-section {
            display: none !important;
          }
        }
      `}</style>

      <section
        className="different-way-section"
        style={{
          backgroundColor: '#fff',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Subtle background prism decorations */}
        <PrismFragment top="60px" left="5%" width={90} height={60} colorFrom="#FBF4BC" colorTo="#F7C08A" rotate={-15} shape="parallelogram-right" opacity={0.15} />
        <PrismFragment top="200px" right="8%" width={70} height={45} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={20} shape="trapezoid-up" opacity={0.12} />
        <PrismFragment bottom="100px" left="3%" width={80} height={50} colorFrom="#F7C08A" colorTo="#F7C08A" rotate={25} shape="rhomboid" opacity={0.1} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Section header */}
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: '600',
              color: '#111111',
              marginBottom: '40px',
              textAlign: 'center'
            }}
          >
            A <WatercolorHighlight gradient="rgba(127, 175, 178, 0.5), rgba(127, 175, 178, 0.3)">different way</WatercolorHighlight> to<br />understand real patients.
          </h2>

          {/* Main content */}
          <div style={{ maxWidth: '750px', margin: '0 auto' }}>
            <p
              style={{
                fontSize: '19px',
                lineHeight: '1.8',
                color: '#444',
                marginBottom: '28px'
              }}
            >
              <span style={{ fontWeight: '600', color: '#111111' }}>Real Patients™</span> captures what traditional patient data leaves out — lived experiences, in a patients' own words.
            </p>

            {/* Callout statement */}
            <div
              style={{
                margin: '40px 0 28px',
                textAlign: 'center'
              }}
            >
              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(22px, 3vw, 28px)',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  color: '#4A4A4A',
                  margin: 0
                }}
              >
                Most healthcare data shows <span style={{ fontWeight: '600', color: '#111111' }}>what happens</span> to patients.
              </h3>
              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(24px, 3.5vw, 32px)',
                  fontWeight: '600',
                  lineHeight: '1.4',
                  color: '#111111',
                  margin: '8px 0 0 0'
                }}
              >
                We focus on{' '}
                <span style={{
                  background: 'linear-gradient(180deg, transparent 55%, rgba(247, 192, 138, 0.5) 55%)',
                  padding: '0 4px'
                }}>what it's like to be one.</span>
              </h3>
            </div>

            {/* Quote to Insight Visual - shows how patient quotes become structured data */}
            <QuoteToInsightVisual />

            {/* Closing statement */}
            <div
              style={{
                padding: '32px 40px',
                backgroundColor: '#111111',
                borderRadius: '16px',
                position: 'relative'
              }}
            >
              <p
                style={{
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: '#fff',
                  margin: 0,
                  textAlign: 'center'
                }}
              >
                Real Patients™ data reflects nuance, tradeoffs, and everyday realities—<br /><span style={{ color: '#F7C08A', fontWeight: '600' }}>structured for modern analytics.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <BrushstrokeDivider flip bgColor="#faf8f6" />

      {/* ========== SECTION: FULL-STACK PATIENT ENGAGEMENT - STACKING CARDS (HIDDEN) ========== */}
      {false && <section
        style={{
          padding: '60px 40px 40px',
          backgroundColor: '#faf8f6',
          position: 'relative',
          zIndex: 10
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(36px, 5vw, 52px)',
                fontWeight: '600',
                color: '#111111',
                marginBottom: '16px'
              }}
            >
              <WatercolorHighlight>Full-Stack</WatercolorHighlight> Patient Engagement™
            </h2>
            <p style={{ fontSize: '20px', color: '#444', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto 24px', fontWeight: '500' }}>
              A unified system for understanding and engaging real patients.
            </p>
            <p style={{ fontSize: '17px', color: '#444', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto' }}>
              Our Full-Stack model replaces fragmented, one-off research with a connected ecosystem of community, data, and modern analytics.
            </p>
          </div>

          {/* Mobile styles for Full Stack cards */}
          <style>{`
            .fullstack-card-wrapper {
              position: sticky;
              margin-bottom: 40px;
            }
            .fullstack-card {
              display: grid;
              grid-template-columns: 56px 1fr 300px;
              min-height: 550px;
            }
            .fullstack-sidebar {
              writing-mode: vertical-rl;
              text-orientation: mixed;
              transform: rotate(180deg);
            }
            .fullstack-illustration {
              display: flex;
            }
            @media (max-width: 900px) {
              .fullstack-card {
                grid-template-columns: 48px 1fr !important;
                min-height: auto !important;
              }
              .fullstack-illustration {
                display: none !important;
              }
              .fullstack-card-wrapper {
                margin-left: 0 !important;
              }
            }
            @media (max-width: 600px) {
              .fullstack-card {
                grid-template-columns: 1fr !important;
              }
              .fullstack-sidebar-container {
                display: none !important;
              }
              .fullstack-top-label {
                display: block !important;
              }
              .fullstack-content {
                padding: 24px 20px !important;
              }
            }
          `}</style>

          {/* Stacking Cards Container - Deck of Cards Effect */}
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: '1100px', maxWidth: '100%' }}>
              {fullStackPillars.map((pillar, index) => {
                const sidebarWidth = 56; // Width of each colored sidebar
                return (
                  <div
                    key={pillar.number}
                    className="fullstack-card-wrapper"
                    style={{
                      top: `calc(25vh + ${index * 15}px)`,
                      zIndex: index + 1,
                      marginLeft: `${index * sidebarWidth}px`, // Each card shifts right to reveal previous sidebars
                    }}
                  >
                    <div
                      className="fullstack-card"
                      style={{
                        backgroundColor: '#fefefe',
                        borderRadius: '16px',
                        boxShadow: '0 12px 50px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Mobile Top Label - shown only on small screens */}
                      <div
                        className="fullstack-top-label"
                        style={{
                          display: 'none',
                          backgroundColor: pillar.color,
                          padding: '12px 20px',
                          gridColumn: '1 / -1'
                        }}
                      >
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#fff',
                          textTransform: 'uppercase',
                          letterSpacing: '2px'
                        }}>
                          {pillar.number} — {pillar.label}
                        </span>
                      </div>

                      {/* Colored Sidebar with Label */}
                      <div
                        className="fullstack-sidebar-container"
                        style={{
                          backgroundColor: pillar.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          padding: '20px 0',
                        }}
                      >
                        <span
                          className="fullstack-sidebar"
                          style={{
                            fontSize: pillar.label.length > 10 ? '20px' : pillar.label.length > 6 ? '24px' : '28px',
                            fontWeight: '800',
                            color: '#fff',
                            textTransform: 'uppercase',
                            letterSpacing: '4px',
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {pillar.label}
                        </span>
                      </div>

                      {/* Content Side */}
                      <div className="fullstack-content" style={{ padding: '36px 32px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {/* Number Badge */}
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '16px',
                          }}
                        >
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              backgroundColor: `${pillar.color}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '15px',
                              fontWeight: '700',
                              color: pillar.color,
                            }}
                          >
                            {pillar.number}
                          </div>
                        </div>

                        {/* Title */}
                        <h3
                          style={{
                            fontFamily: '"Source Serif Pro", Georgia, serif',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: '#111111',
                            marginBottom: '14px',
                            marginTop: 0,
                            lineHeight: '1.2',
                          }}
                        >
                          {pillar.name}
                        </h3>

                        {/* Description */}
                        <p
                          style={{
                            fontSize: '15px',
                            color: '#4A4A4A',
                            lineHeight: '1.7',
                            marginBottom: '20px',
                            marginTop: 0,
                          }}
                        >
                          {pillar.description}
                        </p>

                        {/* Key Points */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {pillar.keyPoints.map((point, i) => (
                            <span
                              key={i}
                              style={{
                                padding: '5px 12px',
                                backgroundColor: `${pillar.color}15`,
                                color: pillar.color,
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: '500',
                              }}
                            >
                              {point}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Illustration Side */}
                      <div
                        className="fullstack-illustration"
                        style={{
                          background: `linear-gradient(135deg, ${pillar.color}15 0%, ${pillar.color}30 100%)`,
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Geometric prism background shapes */}
                        <div style={{ position: 'absolute', top: '-20px', right: '-30px', width: '100px', height: '70px', background: `linear-gradient(135deg, ${pillar.color}30 0%, ${pillar.color}10 100%)`, transform: 'rotate(25deg)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)' }} />
                        <div style={{ position: 'absolute', bottom: '-15px', left: '-20px', width: '80px', height: '60px', background: `linear-gradient(45deg, ${pillar.color}25 0%, transparent 100%)`, transform: 'rotate(-15deg)', clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)' }} />

                        {/* Main Illustration SVG */}
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          {pillar.illustration}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom spacer for stacking */}
          <div style={{ height: '30vh' }} />
        </div>
      </section>}

      <SoftWaveDivider bgColor="#fff" waveColor="rgba(247, 192, 138, 0.35)" />

      {/* ========== SECTION: WHAT THIS SYSTEM MAKES POSSIBLE ========== */}
      <section
        style={{
          padding: '100px 40px',
          backgroundColor: '#faf8f6',
          position: 'relative',
          zIndex: 10
        }}
      >
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 4vw, 48px)',
                fontWeight: '600',
                color: '#111111',
                marginBottom: '16px'
              }}
            >
              Real patient insight, <WatercolorHighlight gradient="rgba(251, 244, 188, 1), rgba(251, 244, 188, 0.85)">designed for the future</WatercolorHighlight>
            </h2>
            <p
              style={{
                fontSize: '18px',
                color: '#4A4A4A',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: '1.7'
              }}
            >
              Patient research has long been built to answer individual questions—but today's healthcare demands insight built to endure.
            </p>
          </div>

          {/* Side-by-side comparison - Traditional vs Real Patients */}
          <div style={{ marginTop: '60px' }}>
            {/* Column Headers */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                marginBottom: '24px'
              }}
            >
              <div
                style={{
                  backgroundColor: '#e8e8e8',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  textAlign: 'center'
                }}
              >
                <h3
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#4A4A4A',
                    margin: 0
                  }}
                >
                  Traditional patient research
                </h3>
              </div>
              <div
                style={{
                  background: 'linear-gradient(135deg, #FABEC0 0%, #F7C08A 50%, #7FAFB2 100%)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  textAlign: 'center'
                }}
              >
                <h3
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111111',
                    margin: 0
                  }}
                >
                  With Real Patients™
                </h3>
              </div>
            </div>

            {/* Comparison Rows */}
            {[
              {
                traditional: 'Small, project-specific samples that change each time',
                realPatients: 'Larger, continuously growing patient communities'
              },
              {
                traditional: 'Patients asked similar questions, repeated across projects',
                realPatients: 'Patients share their experience, and build on it over time'
              },
              {
                traditional: 'Standalone responses, disconnected from broader patient story',
                realPatients: 'Experiences added to broader, longitudinal picture of patient life'
              },
              {
                traditional: 'Patient participation is transactional, minimal value',
                realPatients: 'Patient activation is ongoing, respectful, and high value'
              },
              {
                traditional: 'Data is difficult to reuse or apply to advanced analytics and AI',
                realPatients: 'Data is structured, traceable, and ready for modern analytics and AI'
              }
            ].map((row, index) => (
              <div
                key={index}
                data-index={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px',
                  marginBottom: '16px',
                  opacity: visibleComparisonRows.includes(index) ? 1 : 0,
                  transform: visibleComparisonRows.includes(index) ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.6s ease',
                  transitionDelay: `${index * 0.4}s`
                }}
                className="comparison-row comparison-row-animate"
              >
                {/* Traditional - muted/gray */}
                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                >
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#e0e0e0',
                      color: '#999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      flexShrink: 0
                    }}
                  >
                    ✕
                  </span>
                  <span style={{ fontSize: '16px', color: '#4A4A4A', lineHeight: '1.5' }}>
                    {row.traditional}
                  </span>
                </div>

                {/* Real Patients - vibrant/highlighted */}
                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '2px solid #7FAFB2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    boxShadow: '0 4px 12px rgba(127, 175, 178, 0.15)'
                  }}
                >
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#111111',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      flexShrink: 0
                    }}
                  >
                    ✓
                  </span>
                  <span style={{ fontSize: '16px', color: '#111111', lineHeight: '1.5', fontWeight: '500' }}>
                    {row.realPatients}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile responsive styles */}
          <style>{`
            @media (max-width: 768px) {
              .comparison-row {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>

          {/* Closing statement */}
          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <p
              style={{
                fontSize: '19px',
                lineHeight: '1.7',
                color: '#111111',
                fontWeight: '500',
                maxWidth: '700px',
                margin: '0 auto',
                padding: '24px 32px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid rgba(39, 39, 38, 0.1)'
              }}
            >
              We know what works because we've spent a<br /><span style={{ color: '#F7C08A', fontWeight: '600' }}>decade building alongside patients and healthcare organizations</span>.
            </p>
          </div>
        </div>
      </section>

      <HighlighterBarDivider bgColor="#faf8f6" width="320px" />

      {/* ========== EVOLUTION OF REAL PATIENTS ========== */}
      <InteractiveTimeline milestones={milestones} WatercolorHighlight={WatercolorHighlight} />

      {/* ========== SECTION: WHY THIS MATTERS NOW ========== */}
      <section
        style={{
          padding: '100px 40px',
          backgroundColor: '#111111',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Subtle background decorations */}
        <PrismFragment top="60px" right="8%" width={100} height={70} colorFrom="rgba(247, 192, 138, 0.12)" colorTo="rgba(247, 192, 138, 0.04)" rotate={15} shape="parallelogram-right" opacity={1} />
        <PrismFragment bottom="100px" left="5%" width={80} height={55} colorFrom="rgba(127, 175, 178, 0.1)" colorTo="rgba(127, 175, 178, 0.03)" rotate={-18} shape="trapezoid-up" opacity={1} />

        <div style={{ maxWidth: '850px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '32px',
              textAlign: 'center'
            }}
          >
            Why this matters{' '}
            <WatercolorHighlight gradient="rgba(247, 192, 138, 0.5), rgba(247, 192, 138, 0.4)">now.</WatercolorHighlight>
          </h2>

          {/* Opening */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '48px'
            }}
          >
            <p
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 3.5vw, 38px)',
                fontWeight: '600',
                color: '#F7C08A',
                margin: '0 0 24px 0',
                letterSpacing: '-0.5px',
                textShadow: '0 2px 20px rgba(247, 192, 138, 0.3)'
              }}
            >
              Healthcare has entered a new phase.
            </p>
            <p style={{
              fontSize: '19px',
              lineHeight: '1.8',
              color: 'rgba(255, 255, 255, 0.85)',
              margin: '0 0 20px 0',
              maxWidth: '700px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Patient experience is <span style={{ fontWeight: '600' }}>changing fast</span>—shaped by new treatments, shifting expectations, and technologies that are redefining how decisions get made.
            </p>
            <p style={{
              fontSize: '19px',
              lineHeight: '1.8',
              color: 'rgba(255, 255, 255, 0.85)',
              margin: 0,
              fontWeight: '500'
            }}>
              Patient insight has to change with it.
            </p>
          </div>

          {/* Sequential reveal points */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0',
              marginBottom: '48px'
            }}
          >
            {[
              { text: 'Patients expect engagement that\'s meaningful—not repetitive.', color: '#F7C08A' },
              { text: 'Teams need faster insights that persist through internal change.', color: '#FBF4BC' },
              { text: 'Regulators expect patient data that is relevant and representative.', color: '#7FAFB2' },
              { text: 'AI systems need structured, real patient data to deliver value.', color: '#F7C08A' }
            ].map((item, index) => (
              <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  className="why-matters-reveal"
                  data-index={index}
                  style={{
                    padding: '20px 32px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    borderLeft: `4px solid ${item.color}`,
                    maxWidth: '500px',
                    opacity: visibleWhyMattersItems.includes(index) ? 1 : 0,
                    transform: visibleWhyMattersItems.includes(index) ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease'
                  }}
                >
                  <span style={{ fontSize: '17px', lineHeight: '1.6', color: 'rgba(255, 255, 255, 0.9)' }}>
                    {item.text}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontSize: '24px',
                      margin: '16px 0',
                      opacity: visibleWhyMattersItems.includes(index) ? 1 : 0,
                      transition: 'opacity 0.5s ease 0.3s'
                    }}
                  >
                    ↓
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Callout Box */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(247, 192, 138, 0.15) 0%, rgba(247, 192, 138, 0.1) 100%)',
              border: '1px solid rgba(247, 192, 138, 0.3)',
              borderRadius: '16px',
              padding: '32px 40px',
              marginBottom: '40px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle corner accent */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, rgba(247, 192, 138, 0.2) 0%, transparent 60%)',
                borderRadius: '16px 0 0 0'
              }}
            />
            <p
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(18px, 2.5vw, 22px)',
                lineHeight: '1.7',
                color: '#fff',
                margin: 0,
                position: 'relative',
                zIndex: 1
              }}
            >
              The <span style={{ fontWeight: '600', color: '#F7C08A' }}>Full-Stack Patient Engagement™</span> model was built for this moment—so as healthcare becomes faster and more data-driven, <span style={{ fontWeight: '600' }}>it stays connected to real patient experience.</span>
            </p>
          </div>

          {/* Emphasis text */}
          <div style={{ textAlign: 'center' }}>
            <p
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(24px, 3vw, 32px)',
                fontWeight: '600',
                color: '#fff',
                lineHeight: '1.5',
                margin: 0
              }}
            >
              <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Not assumptions.</span>
              <br />
              <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Not proxies.</span>
              <br />
              <span style={{ color: '#F7C08A' }}>Real patients.</span>
            </p>
          </div>
        </div>
      </section>

      <PrismScatterDivider bgColor="#faf8f6" />

      {/* ========== SECTION: BUILT BY THERAPEUTIC AREA ========== */}
      <section
        id="therapeutic-areas"
        className="living-data-section"
        style={{
          backgroundColor: '#faf8f6',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Background decorations */}
        <PrismFragment top="80px" left="5%" width={90} height={60} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={-15} shape="parallelogram-right" opacity={0.12} />
        <PrismFragment bottom="120px" right="8%" width={80} height={50} colorFrom="#FBF4BC" colorTo="#F7C08A" rotate={20} shape="trapezoid-up" opacity={0.1} />

        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 4vw, 48px)',
                fontWeight: '600',
                color: '#111111',
                marginBottom: '24px'
              }}
            >
              <WatercolorHighlight gradient="rgba(250, 190, 192, 0.7), rgba(250, 190, 192, 0.5)">Living Data.</WatercolorHighlight>{' '}
              <WatercolorHighlight gradient="rgba(247, 192, 138, 0.7), rgba(247, 192, 138, 0.5)">Shared Value.</WatercolorHighlight>
            </h2>
          </div>

          {/* Intro */}
          <p
            style={{
              fontSize: '19px',
              lineHeight: '1.8',
              color: '#444',
              textAlign: 'center',
              maxWidth: '850px',
              margin: '0 auto 24px'
            }}
          >
            A shared foundation of patient experience data for cross-functional learning.
          </p>

          {/* Supporting */}
          <p
            style={{
              fontSize: '17px',
              lineHeight: '1.8',
              color: '#4A4A4A',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 60px'
            }}
          >
            Built to support insight across healthcare organizations—without trapping it in a single function.
          </p>

          {/* Interactive Function Prism */}
          <FunctionPrismVisualization />

          {/* Section closing statement */}
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(28px, 3.5vw, 40px)',
              fontWeight: '600',
              color: '#111111',
              textAlign: 'center',
              marginTop: '60px',
              marginBottom: '0'
            }}
          >
            Built by condition.{' '}
            <WatercolorHighlight gradient="rgba(127, 175, 178, 0.5), rgba(127, 175, 178, 0.3)">Data that grows over time.</WatercolorHighlight>
          </h2>
        </div>
      </section>

      {/* Prism-inspired divider */}
      <div
        style={{
          position: 'relative',
          height: '80px',
          backgroundColor: '#f9f9f9',
          overflow: 'hidden'
        }}
      >
        <svg
          width="100%"
          height="80"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="dividerGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7FAFB2" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#F7C08A" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#FBF4BC" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {/* Top edge from previous section */}
          <polygon
            points="0,0 1440,0 1440,20 720,50 0,20"
            fill="#faf8f6"
          />
          {/* Geometric accent shapes */}
          <polygon
            points="680,25 720,50 760,25"
            fill="url(#dividerGradient1)"
          />
          <polygon
            points="640,20 680,35 660,20"
            fill="#7FAFB2"
            opacity="0.4"
          />
          <polygon
            points="760,20 780,35 800,20"
            fill="#F7C08A"
            opacity="0.4"
          />
        </svg>
      </div>

      <SoftWaveDivider bgColor="#faf8f6" waveColor="rgba(127, 175, 178, 0.25)" />

      {/* ========== SECTION 5: PRISM WAITLIST ========== */}
      <section
        id="express-interest"
        style={{
          padding: '60px 40px 80px',
          backgroundColor: '#f9f9f9',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Background prism decorations */}
        <PrismFragment top="40px" left="8%" width={100} height={35} colorFrom="#FABEC0" colorTo="#F7C08A" rotate={-20} shape="parallelogram-right" opacity={0.25} />
        <PrismFragment top="100px" right="5%" width={80} height={30} colorFrom="#FBF4BC" colorTo="#F7C08A" rotate={25} shape="trapezoid-up" opacity={0.2} />
        <PrismFragment bottom="80px" left="3%" width={90} height={35} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={35} shape="rhomboid" opacity={0.18} />
        <PrismFragment bottom="120px" right="10%" width={75} height={28} colorFrom="#FABEC0" colorTo="#FBF4BC" rotate={-15} shape="parallelogram-left" opacity={0.2} />

        <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 4vw, 44px)',
                fontWeight: '600',
                color: '#111111',
                marginBottom: '16px'
              }}
            >
              Express interest by{' '}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '-3%',
                    right: '-5%',
                    bottom: '5%',
                    height: '40%',
                    background: 'linear-gradient(90deg, rgba(250, 190, 192, 0.5) 0%, rgba(247, 192, 138, 0.4) 100%)',
                    transform: 'skewX(-2deg)',
                    zIndex: 0,
                    borderRadius: '3px 6px 5px 3px'
                  }}
                />
                <span style={{ position: 'relative', zIndex: 1 }}>therapeutic area.</span>
              </span>
            </h2>
            <p style={{ fontSize: '18px', color: '#4A4A4A', maxWidth: '650px', margin: '0 auto 16px' }}>
              Let us know where your focus lies, and we'll explore alignment around foundational access, custom activation, or future collaboration.
            </p>
            <p style={{ fontSize: '15px', color: '#777', maxWidth: '600px', margin: '0 auto', fontStyle: 'italic' }}>
              Expressing interest does not commit you to a program. It allows us to understand demand and share relevant opportunities as communities and datasets expand.
            </p>
          </div>

          {formSubmitted ? (
            <div
              style={{
                backgroundColor: '#FAF8F6',
                padding: '48px',
                borderRadius: '16px',
                textAlign: 'center',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                border: '1px solid #E8E8E8'
              }}
            >
              {/* Success checkmark */}
              <div style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: '#111111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 6px 20px rgba(127, 175, 178, 0.3)'
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <h3 style={{ color: '#111111', fontSize: '28px', marginBottom: '12px', fontFamily: '"Source Serif Pro", Georgia, serif', fontWeight: '600' }}>
                Thanks for reaching out!
              </h3>
              <p style={{ color: '#666666', fontSize: '16px', maxWidth: '400px', margin: '0 auto 32px' }}>
                We've received your information and will be in touch within 24 hours.
              </p>

              {/* Book a Call CTA */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '28px',
                marginBottom: '24px',
                border: '1px solid #E8E8E8'
              }}>
                <p style={{ color: '#111111', fontSize: '18px', fontWeight: '600', marginBottom: '16px', fontFamily: '"Source Serif Pro", Georgia, serif' }}>
                  Ready to talk now?
                </p>
                <a
                  href={process.env.REACT_APP_HUBSPOT_MEETING_LINK || 'mailto:partners@realpatients.com?subject=Schedule a Discovery Call'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#111111',
                    color: '#fff',
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(39, 39, 38, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.backgroundColor = '#3a3a39';
                    e.target.style.boxShadow = '0 6px 20px rgba(39, 39, 38, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.backgroundColor = '#111111';
                    e.target.style.boxShadow = '0 4px 12px rgba(39, 39, 38, 0.2)';
                  }}
                >
                  Book a Discovery Call
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <p style={{ color: '#888888', fontSize: '14px' }}>
                Questions? Email us at <a href="mailto:partners@realpatients.com" style={{ color: '#F7C08A', textDecoration: 'none', fontWeight: '500' }}>partners@realpatients.com</a>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Therapeutic area grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '12px',
                  marginBottom: '40px'
                }}
              >
                {therapeuticAreas.map((area) => (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => togglePrism(area.id)}
                    style={{
                      padding: '20px 16px',
                      borderRadius: '12px',
                      border: selectedPrisms.includes(area.id)
                        ? `2px solid ${area.color}`
                        : '2px solid #e0e0e0',
                      backgroundColor: selectedPrisms.includes(area.id)
                        ? `${area.color}15`
                        : '#fff',
                      color: '#111111',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center',
                      boxShadow: selectedPrisms.includes(area.id)
                        ? `0 2px 8px ${area.color}30`
                        : '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    {/* Color accent bar */}
                    <div style={{
                      width: '40px',
                      height: '4px',
                      backgroundColor: area.color,
                      borderRadius: '2px',
                      margin: '0 auto 12px',
                      opacity: selectedPrisms.includes(area.id) ? 1 : 0.4
                    }} />
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{area.name}</div>
                    {selectedPrisms.includes(area.id) && (
                      <div style={{ fontSize: '12px', color: area.color, marginTop: '8px', fontWeight: '600' }}>✓ Selected</div>
                    )}
                  </button>
                ))}
              </div>

              {/* Contact fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Qualifying fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Your Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    style={{
                      ...inputStyle,
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 16px center'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  >
                    <option value="">Select your role</option>
                    <option value="researcher">Researcher / Scientist</option>
                    <option value="product">Product / UX</option>
                    <option value="commercial">Commercial / Marketing</option>
                    <option value="executive">Executive / Leadership</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Company Size *
                  </label>
                  <select
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                    required
                    style={{
                      ...inputStyle,
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 16px center'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  >
                    <option value="">Select company size</option>
                    <option value="1-50">1-50 employees</option>
                    <option value="51-500">51-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Timeline *
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    required
                    style={{
                      ...inputStyle,
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 16px center'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  >
                    <option value="">When do you need insights?</option>
                    <option value="exploring">Just exploring</option>
                    <option value="1-3-months">Within 1-3 months</option>
                    <option value="immediate">Immediate need</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Primary Interest *
                  </label>
                  <select
                    name="primaryInterest"
                    value={formData.primaryInterest}
                    onChange={handleInputChange}
                    required
                    style={{
                      ...inputStyle,
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 16px center'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  >
                    <option value="">What are you looking for?</option>
                    <option value="clinical-trials">Clinical trial insights</option>
                    <option value="market-research">Market research</option>
                    <option value="patient-journey">Patient journey mapping</option>
                    <option value="product-development">Product development</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', color: '#4A4A4A', fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                  Anything else we should know?
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FABEC0'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Submit button with clear call-to-action */}
              <div style={{
                marginTop: '16px',
                padding: '24px',
                backgroundColor: '#FAF8F6',
                borderRadius: '12px',
                border: '2px dashed #e0e0e0'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: '#4A4A4A',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  {selectedPrisms.length > 0
                    ? `You've selected ${selectedPrisms.length} therapeutic area${selectedPrisms.length > 1 ? 's' : ''}.`
                    : 'Select your therapeutic areas above (optional), then submit.'}
                </p>
                {formError && (
                  <div style={{
                    backgroundColor: '#FDECEC',
                    border: '1px solid #F5B5B5',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    marginBottom: '16px',
                    color: '#9B2C2C',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    Something went wrong sending your request. Please try again, or email{' '}
                    <a href="mailto:partners@realpatients.com?subject=Partnership interest" style={{ color: '#9B2C2C', fontWeight: '600' }}>
                      partners@realpatients.com
                    </a>.
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '20px',
                    fontSize: '18px',
                    fontWeight: '600',
                    backgroundColor: submitting ? '#888888' : '#111111',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (submitting) return;
                    e.target.style.backgroundColor = '#3a3a39';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    if (submitting) return;
                    e.target.style.backgroundColor = '#111111';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {submitting ? 'Sending…' : 'Express interest'}
                  {!submitting && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <GradientFadeDivider fromColor="#f9f9f9" toColor="#a8c5d9" />

      {/* ========== CLOSING SECTION: The Future ========== */}
      <section
        style={{
          padding: '100px 40px',
          backgroundColor: '#a8c5d9',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Subtle geometric accents */}
        <PrismFragment top="60px" left="5%" width={120} height={40} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={-12} shape="parallelogram-right" opacity={0.2} />
        <PrismFragment bottom="80px" right="8%" width={100} height={35} colorFrom="#F7C08A" colorTo="#F7C08A" rotate={20} shape="trapezoid-up" opacity={0.18} />

        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '32px',
              lineHeight: '1.2'
            }}
          >
            The future is led by{' '}
            <span style={{ position: 'relative', display: 'inline-block' }}>
              <span
                style={{
                  position: 'absolute',
                  left: '-3%',
                  right: '-3%',
                  bottom: '8%',
                  height: '35%',
                  background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
                  transform: 'skewX(-2deg)',
                  zIndex: 0,
                  borderRadius: '3px'
                }}
              />
              <span style={{ position: 'relative', zIndex: 1 }}>real patients.</span>
            </span>
          </h2>

          <p
            style={{
              fontSize: 'clamp(17px, 2vw, 19px)',
              color: '#2a2a2a',
              lineHeight: '1.8',
              marginBottom: '40px',
              maxWidth: '720px',
              margin: '0 auto 40px'
            }}
          >
            Built through years of partnership, Real Patients™ helps healthcare organizations turn lived experience into insight that endures—across projects, programs, and what comes next.
          </p>
        </div>
      </section>

      {/* ========== SECTION 7: FINAL CTA ========== */}
      <section
        style={{
          padding: '60px 40px',
          backgroundColor: '#faf8f6',
          borderTop: '1px solid #eee',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10
        }}
      >
        {/* Subtle background prism decorations */}
        <PrismFragment top="20px" left="10%" width={100} height={32} colorFrom="#FFE4D9" colorTo="#FFCBB8" rotate={-15} shape="parallelogram-right" opacity={0.2} />
        <PrismFragment bottom="15px" right="12%" width={110} height={35} colorFrom="#FFCBB8" colorTo="#FFB4A0" rotate={18} shape="trapezoid-down" opacity={0.18} />
        <PrismFragment top="40%" left="5%" width={85} height={45} colorFrom="#FFB4A0" colorTo="#F7C08A" rotate={28} shape="rhomboid" opacity={0.15} />
        <PrismFragment top="30%" right="8%" width={95} height={30} colorFrom="#7FAFB2" colorTo="#7FAFB2" rotate={-22} shape="parallelogram-left" opacity={0.15} />

        <h3
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: '28px',
            fontWeight: '600',
            color: '#111111',
            marginBottom: '24px'
          }}
        >
          Ready to hear from real patients?
        </h3>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a
            href="#express-interest"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('express-interest')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              backgroundColor: '#111111',
              color: '#fff',
              padding: '16px 32px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#111111'}
          >
            Learn More
          </a>
        </div>
      </section>

      {/* Footer */}
      <Footer {...footerProps} />
    </div>
  );
};

export default PartnersLanding;
